import React, { useState, useEffect, useRef } from 'react';
import { Moon, Sun, Sparkles, LogOut, Terminal, Copy, Check, Search, Folder, Trash2, Hash, Edit3, X, Save, Plus, Lock, Globe, Shield, Users, Tag as TagIcon, Settings, Code, FolderOpen, Edit, ChevronRight, ChevronDown, FolderClosed, History, RotateCcw, Menu, HelpCircle, BookOpen, RefreshCw, AlertTriangle, CopyPlus, Pin, Share2, LayoutGrid, List, HardDrive, Download, Upload } from 'lucide-react';
import * as Diff from 'diff';
import { motion, AnimatePresence } from 'framer-motion';

// API endpoint — uses a relative path so it works from any IP or domain.
// Nginx in the frontend container proxies /api → backend:5000/api automatically.
// For local dev outside Docker, override with: const API = "http://localhost:5000/api";
const API = "/api";

const CodeWithImages = ({ code, themeMode }) => {
  if (!code) return null;
  // Regex strictly matches image markdown: ![alt](url)
  const parts = code.split(/(!\[.*?\]\(.*?\))/g);
  return (
    <code>
      {parts.map((part, i) => {
        const match = part.match(/!\[(.*?)\]\((.*?)\)/);
        if (match) {
          return (
            <img
              key={i}
              src={match[2]}
              alt={match[1]}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                display: 'block',
                margin: '10px 0',
                borderRadius: '8px',
                border: themeMode === 'dark' ? '1px solid #333' : '1px solid #e2e8f0'
              }}
            />
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </code>
  );
};


// User Guide Component
const UserGuide = ({ theme, themeMode }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const toggleSection = (idx) => setExpandedSection(expandedSection === idx ? null : idx);

  const sectionStyle = (isOpen) => ({
    background: theme.card,
    border: `1px solid ${isOpen ? theme.accent + '60' : theme.border}`,
    borderRadius: '16px',
    marginBottom: '12px',
    overflow: 'hidden',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    boxShadow: isOpen ? `0 0 0 3px ${theme.accent}15` : theme.shadow
  });

  const headerStyle = {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '20px 24px', cursor: 'pointer', userSelect: 'none'
  };

  const contentStyle = (isOpen) => ({
    maxHeight: isOpen ? '2000px' : '0',
    opacity: isOpen ? 1 : 0,
    overflow: 'hidden',
    transition: 'max-height 0.4s ease, opacity 0.3s ease',
    padding: isOpen ? '0 24px 24px 24px' : '0 24px'
  });

  const stepBoxStyle = {
    display: 'flex', gap: '14px', alignItems: 'flex-start',
    padding: '14px 16px', borderRadius: '10px',
    background: themeMode === 'dark' ? '#1a1a1f' : '#f8f7ff',
    border: `1px solid ${theme.border}`,
    marginBottom: '10px'
  };

  const stepNumStyle = {
    background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
    color: '#fff', fontWeight: '800', fontSize: '11px',
    width: '24px', height: '24px', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, marginTop: '1px'
  };

  const tipStyle = {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '12px 16px', borderRadius: '10px',
    background: `${theme.accent}08`, border: `1px solid ${theme.accent}20`,
    marginTop: '12px', fontSize: '13px', color: theme.text, lineHeight: '1.5'
  };

  const kbdStyle = {
    display: 'inline-block', padding: '2px 7px', borderRadius: '5px',
    background: themeMode === 'dark' ? '#2a2a30' : '#eee',
    border: `1px solid ${theme.border}`, fontSize: '11px',
    fontFamily: 'JetBrains Mono, monospace', fontWeight: '600',
    lineHeight: '1.6', margin: '0 2px'
  };

  const sections = [
    {
      title: 'Getting Started',
      emoji: '🚀',
      icon: <Terminal size={20} color={theme.accent} />,
      summary: 'Login, navigate the dashboard, and understand the interface',
      content: (
        <div>
          <p style={{ color: theme.muted, fontSize: '14px', lineHeight: '1.7', marginBottom: '16px' }}>Snippet Vault is your team's central code library. Here's how to get up and running in under a minute.</p>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', color: theme.text }}>Logging In</h4>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>1</div>
            <div><strong style={{ fontSize: '13px' }}>Open the app</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Navigate to the Snippet Vault URL in your browser (e.g., <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', background: `${theme.accent}10`, padding: '2px 6px', borderRadius: '4px' }}>http://your-server:3000</code>).</p></div>
          </div>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>2</div>
            <div><strong style={{ fontSize: '13px' }}>Enter credentials</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Type your <strong>username</strong> and <strong>password</strong> provided by your administrator. Check <strong>Remember Me</strong> to stay signed in.</p></div>
          </div>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>3</div>
            <div><strong style={{ fontSize: '13px' }}>Click Sign In</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>You'll land on the <strong>Library</strong> — your main dashboard showing all accessible snippets.</p></div>
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '20px', color: theme.text }}>Understanding the Interface</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7', marginBottom: '12px' }}>The interface has three main areas:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
            {[{ label: 'Sidebar (Left)', desc: 'Folders, navigation, admin tools' }, { label: 'Header (Top)', desc: 'Search bar, filters, view toggle, theme' }, { label: 'Main Area (Center)', desc: 'Snippet cards in grid or list view' }].map((area, i) => (
              <div key={i} style={{ padding: '12px', borderRadius: '10px', background: `${theme.accent}08`, border: `1px solid ${theme.accent}15`, textAlign: 'center' }}>
                <p style={{ fontWeight: '700', fontSize: '12px', color: theme.accent, marginBottom: '4px' }}>{area.label}</p>
                <p style={{ fontSize: '12px', color: theme.muted }}>{area.desc}</p>
              </div>
            ))}
          </div>
          <div style={tipStyle}>
            <Sparkles size={16} color={theme.accent} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span><strong>Tip:</strong> Toggle between <strong>Light</strong>, <strong>Dark</strong>, and <strong>Cosmos</strong> themes using the theme button (☀️/🌙/✨) in the top-right corner.</span>
          </div>
        </div>
      )
    },
    {
      title: 'Creating Your First Snippet',
      emoji: '📝',
      icon: <Edit3 size={20} color={theme.accent} />,
      summary: 'Step-by-step guide to creating, editing, and saving code snippets',
      content: (
        <div>
          <p style={{ color: theme.muted, fontSize: '14px', lineHeight: '1.7', marginBottom: '16px' }}>Snippets are the core of Vault. Each snippet stores a piece of code along with metadata like language, tags, and visibility.</p>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>1</div>
            <div><strong style={{ fontSize: '13px' }}>Click "Create Snippet"</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Find the <strong>+ Create Snippet</strong> button in the top-right of the header bar. Keyboard shortcut: <span style={kbdStyle}>Ctrl</span> + <span style={kbdStyle}>N</span></p></div>
          </div>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>2</div>
            <div><strong style={{ fontSize: '13px' }}>Fill in the form</strong>
              <ul style={{ color: theme.muted, fontSize: '13px', marginTop: '6px', paddingLeft: '16px', lineHeight: '2' }}>
                <li><strong>Title</strong> (required) — A clear, descriptive name</li>
                <li><strong>Description</strong> — What the code does, context, or usage notes</li>
                <li><strong>Language</strong> — Select the programming language for syntax highlighting</li>
                <li><strong>Folder</strong> — Organize into a category (e.g., "Scripts", "Networking")</li>
                <li><strong>Tags</strong> — Click tags to add searchable keywords</li>
                <li><strong>Visibility</strong> — Public, Private, or Internal</li>
                <li><strong>Code</strong> (required) — Paste or type your code</li>
              </ul>
            </div>
          </div>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>3</div>
            <div><strong style={{ fontSize: '13px' }}>Save your snippet</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Click <strong>Save Snippet</strong> at the bottom. Your snippet will immediately appear in the library.</p></div>
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '20px', color: theme.text }}>Image Support</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7' }}>You can <strong>paste images directly</strong> into the code editor (Ctrl+V) or click the image upload button. Images are stored on the server and embedded using markdown syntax: <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: `${theme.accent}10`, padding: '2px 6px', borderRadius: '4px' }}>![alt](url)</code></p>
          <div style={tipStyle}>
            <Sparkles size={16} color={theme.accent} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span><strong>Tip:</strong> Use the <strong>Preview</strong> toggle in the editor to see how your images will render before saving.</span>
          </div>
        </div>
      )
    },
    {
      title: 'Viewing & Interacting with Snippets',
      emoji: '👁️',
      icon: <Code size={20} color={theme.accent} />,
      summary: 'Preview, copy, pin, duplicate, and manage your code cards',
      content: (
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', color: theme.text }}>Snippet Cards</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7', marginBottom: '12px' }}>Each snippet shows as a card with its title, metadata (folder, language, author, tags), and a live code preview. Here's what you can do:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', marginBottom: '16px' }}>
            {[
              { icon: '👆', action: 'Click card body', result: 'Copy code to clipboard' },
              { icon: '🔗', action: 'Click title', result: 'Open full-screen preview' },
              { icon: '📌', action: 'Pin button', result: 'Pin snippet to top' },
              { icon: '✏️', action: 'Edit button', result: 'Open edit form' },
              { icon: '📋', action: 'Duplicate button', result: 'Create a copy' },
              { icon: '🗑️', action: 'Delete button', result: 'Move to recycle bin' },
              { icon: '🔗', action: 'Share button', result: 'Generate public link' },
              { icon: '🕐', action: 'History button', result: 'View version history' }
            ].map((item, i) => (
              <div key={i} style={{ padding: '10px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
                <p style={{ fontSize: '16px', marginBottom: '4px' }}>{item.icon}</p>
                <p style={{ fontWeight: '700', fontSize: '12px', color: theme.text }}>{item.action}</p>
                <p style={{ fontSize: '11px', color: theme.muted }}>{item.result}</p>
              </div>
            ))}
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', color: theme.text }}>Preview Modal</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7', marginBottom: '8px' }}>When you click a snippet's <strong>title</strong>, a full-screen preview opens styled like a code editor. Features include:</p>
          <ul style={{ color: theme.muted, fontSize: '13px', paddingLeft: '16px', lineHeight: '2' }}>
            <li>Full scrollable code with no height restrictions</li>
            <li>Quick <strong>Copy</strong> and <strong>Edit</strong> buttons in the header</li>
            <li>Metadata strip showing folder, language, author, visibility, and tags</li>
            <li>Status bar with line count and language indicator</li>
          </ul>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '16px', color: theme.text }}>View Modes</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7' }}>Toggle between <strong>Grid View</strong> (cards with code preview) and <strong>List View</strong> (compact table rows) using the view toggle buttons in the header. Your preference is saved automatically.</p>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '16px', color: theme.text }}>Drag-and-Drop Reordering</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7' }}>Drag snippets by the <strong>grip dots</strong> (⠿) on the left of each card to reorder them. Your custom order is saved locally. On mobile, use the up/down arrow controls instead.</p>
        </div>
      )
    },
    {
      title: 'Search & Filters',
      emoji: '🔍',
      icon: <Search size={20} color={theme.accent} />,
      summary: 'Find any snippet instantly using search, tags, filters, and folders',
      content: (
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', color: theme.text }}>Search Bar</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7', marginBottom: '12px' }}>The search bar filters snippets by <strong>title</strong>, <strong>description</strong>, and <strong>tags</strong> in real-time as you type.</p>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}><Search size={12} color="#fff" /></div>
            <div><strong style={{ fontSize: '13px' }}>Full-Code Search</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Click the <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: `${theme.accent}10`, padding: '2px 6px', borderRadius: '4px' }}>&lt;/&gt;</code> button to also search <em>inside</em> the actual code content. This is useful for finding specific function names, variables, or patterns.</p></div>
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '16px', color: theme.text }}>Filter Dropdowns</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7', marginBottom: '12px' }}>Below the search bar, use the dropdown filters to narrow results:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', marginBottom: '12px' }}>
            {[
              { filter: 'Tags', desc: 'Filter by keyword tag' },
              { filter: 'Users', desc: 'Filter by snippet creator' },
              { filter: 'Groups', desc: 'Filter by user group' },
              { filter: 'Visibility', desc: 'Public, Internal, or Private' }
            ].map((f, i) => (
              <div key={i} style={{ padding: '10px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
                <p style={{ fontWeight: '700', fontSize: '12px', color: theme.accent }}>{f.filter}</p>
                <p style={{ fontSize: '12px', color: theme.muted }}>{f.desc}</p>
              </div>
            ))}
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '16px', color: theme.text }}>Folder Navigation</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7' }}>Use the <strong>sidebar folder tree</strong> to browse snippets by folder. Folders can be nested using a forward slash in the name (e.g., <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: `${theme.accent}10`, padding: '2px 6px', borderRadius: '4px' }}>Scripts/Automation</code>). Click a folder to filter the view.</p>
          <div style={tipStyle}>
            <Sparkles size={16} color={theme.accent} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span><strong>Keyboard shortcut:</strong> Press <span style={kbdStyle}>Ctrl</span> + <span style={kbdStyle}>F</span> to jump directly to the search bar.</span>
          </div>
        </div>
      )
    },
    {
      title: 'Folders, Tags & Languages',
      emoji: '📁',
      icon: <FolderOpen size={20} color={theme.accent} />,
      summary: 'Organize your code library with folders, tags, and language categories',
      content: (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <FolderOpen size={18} color={theme.accent} />
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: theme.text }}>Folders</h4>
              </div>
              <ul style={{ color: theme.muted, fontSize: '13px', paddingLeft: '16px', lineHeight: '2' }}>
                <li>Group related snippets together</li>
                <li>Use <code style={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }}>/</code> for nested folders</li>
                <li>Browse via the sidebar tree</li>
                <li>Examples: "General", "Scripts/Backup", "Networking/VPN"</li>
              </ul>
            </div>
            <div style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <TagIcon size={18} color={theme.accent} />
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: theme.text }}>Tags</h4>
              </div>
              <ul style={{ color: theme.muted, fontSize: '13px', paddingLeft: '16px', lineHeight: '2' }}>
                <li>Add searchable keywords to snippets</li>
                <li>Filter by tag using the dropdown</li>
                <li>Admins can create/delete tags</li>
                <li>Examples: #vpn, #hosts, #react, #utility</li>
              </ul>
            </div>
            <div style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <Code size={18} color={theme.accent} />
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: theme.text }}>Languages</h4>
              </div>
              <ul style={{ color: theme.muted, fontSize: '13px', paddingLeft: '16px', lineHeight: '2' }}>
                <li>Choose language for syntax highlighting</li>
                <li>Shown on each snippet card</li>
                <li>Admins can add custom languages</li>
                <li>Examples: Python, JavaScript, Bash, PowerShell</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Sharing & Visibility',
      emoji: '🔒',
      icon: <Shield size={20} color={theme.accent} />,
      summary: 'Control who can see your snippets with visibility settings and share links',
      content: (
        <div>
          <p style={{ color: theme.muted, fontSize: '14px', lineHeight: '1.7', marginBottom: '16px' }}>Every snippet has a visibility level that controls access:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {[
              { icon: <Globe size={20} />, level: 'Public', color: theme.success, desc: 'Visible to all authenticated users. Can generate shareable public links that work without login.' },
              { icon: <Lock size={20} />, level: 'Private', color: theme.danger, desc: 'Only visible to you and Superusers. Use for personal scripts or sensitive code.' },
              { icon: <Shield size={20} />, level: 'Internal', color: theme.accent, desc: 'Visible to you, Superusers, and specific User Groups you select.' }
            ].map((v, i) => (
              <div key={i} style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${v.color}30`, background: `${v.color}08` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: v.color, marginBottom: '8px' }}>
                  {v.icon}
                  <h4 style={{ fontSize: '15px', fontWeight: '700' }}>{v.level}</h4>
                </div>
                <p style={{ fontSize: '12px', color: theme.muted, lineHeight: '1.6' }}>{v.desc}</p>
              </div>
            ))}
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', color: theme.text }}>Public Share Links</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7' }}>For <strong>Public</strong> snippets, click the <strong>Share</strong> button (🔗) to generate a unique URL. Anyone with the link can view the snippet <em>without needing to log in</em>. Share links can be copied and sent via email, Slack, or any communication tool.</p>
        </div>
      )
    },
    {
      title: 'Version History & Recovery',
      emoji: '⏳',
      icon: <History size={20} color={theme.accent} />,
      summary: 'Track every change and restore previous versions of any snippet',
      content: (
        <div>
          <p style={{ color: theme.muted, fontSize: '14px', lineHeight: '1.7', marginBottom: '16px' }}>Every time you save a snippet, a new version is automatically created. You'll never lose your work.</p>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>1</div>
            <div><strong style={{ fontSize: '13px' }}>Open Version History</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Click the <strong>clock icon</strong> (🕐) on any snippet card to view its change history.</p></div>
          </div>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>2</div>
            <div><strong style={{ fontSize: '13px' }}>Review Changes</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Each version shows a <strong>color-coded diff</strong> — green for additions, red for removals — along with the version number, timestamp, and who made the change.</p></div>
          </div>
          <div style={stepBoxStyle}>
            <div style={stepNumStyle}>3</div>
            <div><strong style={{ fontSize: '13px' }}>Restore a Version</strong><p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Click <strong>"Restore This Version"</strong> to revert the snippet to any previous state. The current version is saved as a new version before restoring.</p></div>
          </div>
          <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', marginTop: '16px', color: theme.text }}>Recycle Bin</h4>
          <p style={{ color: theme.muted, fontSize: '13px', lineHeight: '1.7' }}>Deleted snippets are moved to the <strong>Recycle Bin</strong> (accessible in the sidebar) and kept for <strong>60 days</strong>. You can restore them at any time before they're permanently removed. Superusers can empty the recycle bin manually.</p>
        </div>
      )
    },
    {
      title: 'User Roles & Permissions',
      emoji: '👥',
      icon: <Users size={20} color={theme.accent} />,
      summary: 'Understand the three permission levels: User, Admin, and Superuser',
      content: (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {[
              { role: 'Regular User', badge: '👤', capabilities: ['Create, edit, delete own snippets', 'View public and shared snippets', 'Pin and organize personal library', 'Copy and share public links'] },
              { role: 'Admin', badge: '🛡️', capabilities: ['All Regular User capabilities', 'Create and manage users', 'Create and manage groups', 'Manage tags and languages'] },
              { role: 'Superuser', badge: '⚡', capabilities: ['Full system access', 'View ALL snippets (even private)', 'Access analytics dashboard', 'Manage backups', 'Empty recycle bin'] }
            ].map((r, i) => (
              <div key={i} style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
                <p style={{ fontSize: '24px', marginBottom: '8px' }}>{r.badge}</p>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: theme.text, marginBottom: '10px' }}>{r.role}</h4>
                <ul style={{ color: theme.muted, fontSize: '12px', paddingLeft: '14px', lineHeight: '2' }}>
                  {r.capabilities.map((c, j) => <li key={j}>{c}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      title: 'Admin Tools',
      emoji: '⚙️',
      icon: <Settings size={20} color={theme.accent} />,
      summary: 'Manage users, groups, tags, languages, analytics, and backups',
      content: (
        <div>
          <p style={{ color: theme.muted, fontSize: '14px', lineHeight: '1.7', marginBottom: '16px' }}>Admin and Superuser accounts have access to management tools in the sidebar:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
            {[
              { label: 'Users', icon: <Users size={16} color={theme.accent} />, desc: 'Create accounts, set roles, assign to groups, reset passwords.' },
              { label: 'Groups', icon: <Shield size={16} color={theme.accent} />, desc: 'Create teams, manage members, control internal snippet access.' },
              { label: 'Tags', icon: <TagIcon size={16} color={theme.accent} />, desc: 'Add or remove searchable keyword tags.' },
              { label: 'Languages', icon: <Code size={16} color={theme.accent} />, desc: 'Add custom programming languages for syntax highlighting.' },
              { label: 'Analytics', icon: <LayoutGrid size={16} color={theme.accent} />, desc: 'View stats: snippet counts, language distribution, top contributors, recent activity.' },
              { label: 'Backups', icon: <HardDrive size={16} color={theme.accent} />, desc: 'Create/restore/download database backups. Set automatic backup schedules.' }
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', padding: '14px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: themeMode === 'dark' ? '#1a1a1f' : '#fafafa' }}>
                <div style={{ background: `${theme.accent}12`, padding: '8px', borderRadius: '8px', height: 'fit-content' }}>{t.icon}</div>
                <div>
                  <p style={{ fontWeight: '700', fontSize: '13px', color: theme.text, marginBottom: '4px' }}>{t.label}</p>
                  <p style={{ fontSize: '12px', color: theme.muted, lineHeight: '1.5' }}>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '40px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${theme.accent}12`, width: '56px', height: '56px', borderRadius: '16px', marginBottom: '16px' }}>
          <BookOpen size={28} color={theme.accent} />
        </div>
        <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>User Guide</h1>
        <p style={{ color: theme.muted, fontSize: '16px', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>Everything you need to know to get the most out of Snippet Vault. Click any section to expand.</p>
      </div>

      <div>
        {sections.map((section, idx) => (
          <div key={idx} style={sectionStyle(expandedSection === idx)}>
            <div style={headerStyle} onClick={() => toggleSection(idx)}>
              <div style={{ background: `${theme.accent}12`, padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {section.icon}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: theme.text }}>{section.emoji} {section.title}</h3>
                <p style={{ fontSize: '13px', color: theme.muted, fontWeight: '500', marginTop: '2px' }}>{section.summary}</p>
              </div>
              <ChevronRight size={18} color={theme.muted} style={{ transition: 'transform 0.3s ease', transform: expandedSection === idx ? 'rotate(90deg)' : 'none', flexShrink: 0 }} />
            </div>
            <div style={contentStyle(expandedSection === idx)}>
              {section.content}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '40px', padding: '24px', background: `${theme.accent}05`, borderRadius: '16px', border: `1px dashed ${theme.accent}30`, textAlign: 'center' }}>
        <p style={{ fontWeight: '600', color: theme.accent }}>Need more help?</p>
        <p style={{ fontSize: '13px', color: theme.muted, marginTop: '4px' }}>Contact your system administrator for technical support or account issues.</p>
      </div>
    </div>
  );
};

export default function App() {
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });

  const [apiToken, setApiToken] = useState(() => {
    return localStorage.getItem('apiToken') || null;
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState(() => {
    return localStorage.getItem('displayMode') || 'grid';
  });

  const [view, setView] = useState('snippets');
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState({ snippets: [], users: [], tags: [], groups: [], languages: [] });
  const [search, setSearch] = useState("");
  const [searchFullCode, setSearchFullCode] = useState(false);
  const [activeFolder, setActiveFolder] = useState("All");
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [shareModal, setShareModal] = useState(null); // { token, url }
  const [publicSnippet, setPublicSnippet] = useState(null);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [recycleBin, setRecycleBin] = useState([]);
  const [recycleBinLoading, setRecycleBinLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupUploading, setBackupUploading] = useState(false);
  const [backupSettings, setBackupSettings] = useState({ scheduleHours: 0, maxBackups: 15 });
  const [sharedCopied, setSharedCopied] = useState(false);

  // What's New popup — show only once per version
  const WHATS_NEW_VERSION = 'v2.5.0';
  const [showWhatsNew, setShowWhatsNew] = useState(() => {
    return localStorage.getItem(`whatsNew_${WHATS_NEW_VERSION}`) !== 'seen';
  });
  const dismissWhatsNew = () => {
    setShowWhatsNew(false);
    localStorage.setItem(`whatsNew_${WHATS_NEW_VERSION}`, 'seen');
  };

  // Filters
  const [filterTag, setFilterTag] = useState("All");
  const [filterUser, setFilterUser] = useState("All");
  const [filterGroup, setFilterGroup] = useState("All");
  const [filterVisibility, setFilterVisibility] = useState("All");

  const [auth, setAuth] = useState({ username: '', password: '', rememberMe: false });
  const [form, setForm] = useState({
    title: '',
    description: '',
    code: '',
    language: 'javascript',
    tags: [],
    folder: 'General',
    visibility: 'public',
    target_groups: [],
    pinned: false
  });
  const [editorPreview, setEditorPreview] = useState(false);

  // Management forms
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    role: 'tech',
    first_name: '',
    last_name: '',
    user_groups: []
  });
  const [editingUser, setEditingUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ userId: null, password: '', oldPassword: '', confirmPassword: '', isSelfChange: false });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '' });
  const [tagForm, setTagForm] = useState({ name: '' });
  const [languageForm, setLanguageForm] = useState({ name: '' });
  const [managingGroup, setManagingGroup] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [showFolderSelector, setShowFolderSelector] = useState(false);
  const [snippetVersions, setSnippetVersions] = useState(null); // { snippetId, versions: [] }

  // Drag-and-drop reordering state
  const [customOrder, setCustomOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snippetOrder') || '[]'); } catch { return []; }
  });
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Real-time: last refresh tracking
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [secondsSince, setSecondsSince] = useState(0);

  // Ref so interval callbacks always see the latest apiToken without stale closure
  const apiTokenRef = useRef(apiToken);
  useEffect(() => { apiTokenRef.current = apiToken; }, [apiToken]);

  // Custom UI Popups State
  const [notification, setNotification] = useState(null); // { message, type: 'success' | 'error' | 'info' }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }
  const [previewSnippet, setPreviewSnippet] = useState(null); // snippet object for full preview

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const confirmAction = (title, message, onConfirm) => {
    setConfirmModal({ title, message, onConfirm });
  };

  const getUserDisplayName = (username) => {
    const u = data.users.find(user => user.username === username);
    if (u && u.first_name && u.last_name) {
      return `${u.first_name} ${u.last_name}`;
    }
    return username;
  };

  const getContrastColor = (hexColor) => {
    return '#000000';
  };

  const theme = {
    bg: themeMode === 'cosmos' ? '#f5f3ff' : (themeMode === 'dark' ? '#0e0e11' : '#fbf8fc'),
    card: themeMode === 'cosmos' ? '#ffffffcc' : (themeMode === 'dark' ? '#19191d' : '#ffffff'),
    border: themeMode === 'cosmos' ? '#e9e4ff' : (themeMode === 'dark' ? 'rgba(72, 71, 75, 0.15)' : '#e8e8ec'),
    text: themeMode === 'cosmos' ? '#2e1065' : (themeMode === 'dark' ? '#fbf8fc' : '#19191d'),
    muted: themeMode === 'cosmos' ? '#8b5cf690' : (themeMode === 'dark' ? '#acaaae' : '#767578'),
    accent: themeMode === 'cosmos' ? '#8b5cf6' : (themeMode === 'dark' ? '#a3a6ff' : '#6366F1'),
    accentHover: themeMode === 'cosmos' ? '#7c3aed' : (themeMode === 'dark' ? '#6063ee' : '#494bd7'),
    danger: themeMode === 'dark' ? '#ff6e84' : '#d73357',
    success: themeMode === 'dark' ? '#47d5a6' : '#10b981',
    shadow: themeMode === 'dark'
      ? '0 24px 48px -12px rgba(163, 166, 255, 0.08)'
      : '0 4px 20px -2px rgba(99, 102, 241, 0.08)',
    shadowLg: themeMode === 'dark'
      ? '0 32px 64px -16px rgba(163, 166, 255, 0.12)'
      : '0 10px 40px -4px rgba(99, 102, 241, 0.12)',
  };

  // Persist theme changes
  useEffect(() => {
    localStorage.setItem('theme', themeMode);
  }, [themeMode]);

  // Persist user changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      refreshData();
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('apiToken');
    }
  }, [user]);

  // Fetch stats when view changes to stats
  useEffect(() => {
    if (view === 'stats' && user) {
      fetch(`${API}/stats`, { headers: getHeaders() })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(d => setStats(d))
        .catch(() => showNotification('Failed to fetch stats', 'error'));
    }
  }, [view, user]);

  // Persist API token
  useEffect(() => {
    if (apiToken) {
      localStorage.setItem('apiToken', apiToken);
    }
  }, [apiToken]);

  // Auto-refresh polling every 5 minutes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      // Use the ref so we always have the latest token
      if (apiTokenRef.current) refreshData();
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-Sent Events (SSE) for real-time updates
  useEffect(() => {
    if (!user || !apiTokenRef.current) return;
    const sse = new EventSource(`${API}/events?token=${apiTokenRef.current}`);

    const handleSnippetUpdate = (e) => {
      try {
        const updatedSnippet = JSON.parse(e.data);
        setData(prev => {
          // Avoid duplicates if the snippet is new
          const snippetExists = prev.snippets.some(s => s.id === updatedSnippet.id);
          if (snippetExists) {
            return {
              ...prev,
              snippets: prev.snippets.map(s =>
                s.id === updatedSnippet.id ? { ...s, ...updatedSnippet } : s
              )
            };
          }
          return prev; // Or handle new snippet addition if needed
        });
      } catch (err) {
        console.error("SSE snippet_updated parse error:", err);
      }
    };

    const refresh = () => refreshData();

    sse.addEventListener('snippet_updated', handleSnippetUpdate);

    // For other events, just refresh all data for now
    sse.addEventListener('comment_added', refresh);
    sse.addEventListener('user_deleted', refresh);
    sse.addEventListener('snippet_deleted', refresh);
    sse.addEventListener('snippet_created', refresh);
    sse.addEventListener('snippet_restored', refresh);

    sse.onmessage = refresh; // Fallback for generic messages

    return () => sse.close();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live 'seconds since last refresh' ticker (updates every second)
  useEffect(() => {
    if (!user) return;
    const tick = setInterval(() => {
      setSecondsSince(prev => prev + 1);
    }, 1000);
    return () => clearInterval(tick);
  }, [user]);

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${apiToken}`
  });

  // Handle public share links (#15)
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/share/')) {
      const token = path.split('/')[2];
      if (token) {
        fetch(`${API}/share/${token}`)
          .then(res => res.ok ? res.json() : Promise.reject())
          .then(snippet => setPublicSnippet(snippet))
          .catch(() => showNotification('Shared snippet not found or link expired', 'error'));
      }
    }
  }, []);

  // Keyboard Shortcuts (#7)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        setEditingId('new');
        setForm({ title: '', description: '', code: '', language: 'javascript', tags: [], folder: 'General', visibility: 'public', target_groups: [], pinned: false });
      }
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.getElementById('main-search');
        if (searchInput) searchInput.focus();
      }
      if (e.key === 'Escape') {
        setEditingId(null);
        setSelectedSnippet(null);
        setShareModal(null);
        setSnippetVersions(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const refreshData = async (isLoadMore = false) => {
    try {
      setLoading(true);
      const nextPage = isLoadMore ? page + 1 : 1;
      const res = await fetch(`${API}/data?page=${nextPage}`, {
        headers: getHeaders()
      });
      if (res.status === 401 || res.status === 403) {
        setUser(null);
        setApiToken(null);
        showNotification('Session expired. Please login again.', 'error');
        return;
      }
      const json = await res.json();

      if (json.snippets && json.snippets.length === 0 && isLoadMore) {
        setHasMore(false);
      } else {
        setHasMore(json.pagination.page < json.pagination.pages);
      }

      setData(prev => ({
        ...json,
        snippets: isLoadMore ? [...prev.snippets, ...json.snippets] : json.snippets
      }));
      setPage(nextPage);
      setLastRefreshed(new Date());
      setSecondsSince(0);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecycleBin = async () => {
    try {
      setRecycleBinLoading(true);
      const res = await fetch(`${API}/trash`, { headers: getHeaders() });
      if (res.ok) {
        const json = await res.json();
        setRecycleBin(json.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch recycle bin:', error);
    } finally {
      setRecycleBinLoading(false);
    }
  };

  const restoreSnippet = async (id) => {
    try {
      const res = await fetch(`${API}/trash/${id}/restore`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        showNotification('Snippet restored', 'success');
        fetchRecycleBin();
        refreshData();
      } else {
        const error = await res.json();
        showNotification(error.error || 'Failed to restore', 'error');
      }
    } catch (error) {
      showNotification('Failed to restore snippet', 'error');
    }
  };

  const permanentDeleteSnippet = async (id) => {
    confirmAction(
      'Permanently delete',
      'This snippet will be gone forever. This cannot be undone.',
      async () => {
        try {
          const res = await fetch(`${API}/trash/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
          });
          if (res.ok) {
            showNotification('Snippet permanently deleted', 'success');
            fetchRecycleBin();
          } else {
            const error = await res.json();
            showNotification(error.error || 'Failed to delete', 'error');
          }
        } catch (error) {
          showNotification('Failed to delete', 'error');
        }
      }
    );
  };

  const emptyRecycleBin = async () => {
    confirmAction(
      'Empty recycle bin',
      'All snippets in the recycle bin will be permanently deleted. This cannot be undone.',
      async () => {
        try {
          const res = await fetch(`${API}/trash`, {
            method: 'DELETE',
            headers: getHeaders()
          });
          if (res.ok) {
            showNotification('Recycle bin emptied', 'success');
            setRecycleBin([]);
          }
        } catch (error) {
          showNotification('Failed to empty recycle bin', 'error');
        }
      }
    );
  };

  const fetchBackups = async () => {
    try {
      setBackupsLoading(true);
      const [backupsRes, settingsRes] = await Promise.all([
        fetch(`${API}/backups`, { headers: getHeaders() }),
        fetch(`${API}/backups/settings`, { headers: getHeaders() })
      ]);
      if (backupsRes.ok) setBackups((await backupsRes.json()).backups || []);
      if (settingsRes.ok) setBackupSettings(await settingsRes.json());
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    } finally {
      setBackupsLoading(false);
    }
  };

  const saveBackupSettings = async (newSettings) => {
    try {
      const res = await fetch(`${API}/backups/settings`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(newSettings)
      });
      if (res.ok) {
        const saved = await res.json();
        setBackupSettings(saved);
        showNotification('Backup settings saved', 'success');
        fetchBackups();
      }
    } catch (error) {
      showNotification('Failed to save settings', 'error');
    }
  };

  const createBackup = async () => {
    try {
      const res = await fetch(`${API}/backups`, { method: 'POST', headers: getHeaders() });
      if (res.ok) {
        const json = await res.json();
        showNotification(`Backup created: ${json.name}`, 'success');
        fetchBackups();
      } else {
        const error = await res.json();
        showNotification(error.error || 'Failed to create backup', 'error');
      }
    } catch (error) {
      showNotification('Failed to create backup', 'error');
    }
  };

  const downloadBackup = async (name) => {
    try {
      const res = await fetch(`${API}/backups/${encodeURIComponent(name)}`, { headers: { 'Authorization': `Bearer ${apiToken}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      showNotification('Failed to download backup', 'error');
    }
  };

  const restoreBackupFromServer = async (name) => {
    confirmAction(
      'Restore database',
      `This will replace the current database with "${name}". A pre-restore backup will be created automatically. The server will restart.`,
      async () => {
        try {
          const res = await fetch(`${API}/backups/${encodeURIComponent(name)}/restore`, {
            method: 'POST',
            headers: getHeaders()
          });
          if (res.ok) {
            showNotification('Database restored successfully', 'success');
            refreshData();
            fetchBackups();
          } else {
            const error = await res.json();
            showNotification(error.error || 'Failed to restore', 'error');
          }
        } catch (error) {
          showNotification('Failed to restore backup', 'error');
        }
      }
    );
  };

  const deleteBackup = async (name) => {
    confirmAction('Delete backup', 'Are you sure you want to delete this backup file?', async () => {
      try {
        const res = await fetch(`${API}/backups/${encodeURIComponent(name)}`, { method: 'DELETE', headers: getHeaders() });
        if (res.ok) {
          showNotification('Backup deleted', 'success');
          fetchBackups();
        }
      } catch (error) {
        showNotification('Failed to delete backup', 'error');
      }
    });
  };

  const restoreBackup = async (file) => {
    confirmAction(
      'Restore database',
      'This will replace the current database with the backup. A pre-restore backup will be created automatically. The server will restart.',
      async () => {
        try {
          setBackupUploading(true);
          const formData = new FormData();
          formData.append('backup', file);
          const res = await fetch(`${API}/backups/restore`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${apiToken}` },
            body: formData
          });
          if (res.ok) {
            showNotification('Database restored successfully', 'success');
            refreshData();
            fetchBackups();
          } else {
            const error = await res.json();
            showNotification(error.error || 'Failed to restore', 'error');
          }
        } catch (error) {
          showNotification('Failed to restore backup', 'error');
        } finally {
          setBackupUploading(false);
        }
      }
    );
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(auth)
      });

      if (res.ok) {
        const userData = await res.json();
        setApiToken(userData.token);
        delete userData.token; // Remove token from user object
        setUser(userData);
        setAuth({ username: '', password: '', rememberMe: false });
        setPage(1);
        setHasMore(true);
      } else {
        const error = await res.json();
        showNotification(error.error || 'Login failed', 'error');
      }
    } catch (error) {
      showNotification('Login failed. Please check your connection.', 'error');
    }
  };

  const handleShare = async (id) => {
    try {
      const res = await fetch(`${API}/snippets/${id}/share`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        const d = await res.json();
        setShareModal(d);
      } else {
        showNotification('Failed to generate share link', 'error');
      }
    } catch (e) {
      showNotification('Sharing failed', 'error');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      const res = await fetch(`${API}/snippets/${id}/pin`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        const d = await res.json();
        showNotification(d.pinned ? 'Snippet pinned to top' : 'Snippet unpinned', 'success');
        setData(prev => ({
          ...prev,
          snippets: prev.snippets.map(s => s.id === id ? { ...s, pinned: d.pinned } : s)
        }));
      } else {
        const errorData = await res.json();
        showNotification(errorData.error || 'Failed to toggle pin', 'error');
      }
    } catch (e) {
      showNotification('Failed to toggle pin', 'error');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.password !== passwordForm.confirmPassword) {
      showNotification('New passwords do not match', 'error');
      return;
    }

    try {
      const res = await fetch(`${API}/change-password`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.password
        })
      });

      const data = await res.json();
      if (res.ok) {
        showNotification('Password changed successfully', 'success');
        setShowPasswordModal(false);
        setPasswordForm({ userId: null, password: '', oldPassword: '', confirmPassword: '', isSelfChange: false });
      } else {
        showNotification(data.error || 'Failed to change password', 'error');
      }
    } catch (error) {
      console.error('Password change error:', error);
      showNotification('An error occurred. Please try again.', 'error');
    }
  };

  const startEdit = (snippet) => {
    setEditingId(snippet.id);
    const snippetTags = snippet.tag ? snippet.tag.split(',').map(t => t.trim()).filter(t => t) : [];
    setForm({
      title: snippet.title,
      description: snippet.description || '',
      code: snippet.code,
      language: snippet.language || 'javascript',
      tags: snippetTags,
      folder: snippet.folder || 'General',
      visibility: snippet.visibility,
      target_groups: snippet.target_groups ? snippet.target_groups.split(',').filter(g => g !== 'All') : [],
      pinned: !!snippet.pinned
    });
    setView('create');
  };

  const duplicateSnippet = (snippet) => {
    setEditingId(null);
    const snippetTags = snippet.tag ? snippet.tag.split(',').map(t => t.trim()).filter(t => t) : [];
    setForm({
      title: `${snippet.title} (Copy)`,
      description: snippet.description || '',
      code: snippet.code,
      language: snippet.language || 'javascript',
      tags: snippetTags,
      folder: snippet.folder || 'General',
      visibility: snippet.visibility,
      target_groups: snippet.target_groups ? snippet.target_groups.split(',').filter(g => g !== 'All') : [],
      pinned: false
    });
    setView('create');
  };

  const handleImagePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (!file) continue;

        const cursorPosition = e.target.selectionStart;
        const textBefore = form.code.substring(0, cursorPosition);
        const textAfter = form.code.substring(e.target.selectionEnd);

        const placeholder = `![Uploading ${file.name}...]()\n`;
        setForm(prev => ({ ...prev, code: textBefore + placeholder + textAfter }));

        const formData = new FormData();
        formData.append('image', file);

        try {
          const res = await fetch(`${API}/upload`, {
            method: 'POST',
            body: formData,
            headers: {
              ...(apiTokenRef.current ? { 'Authorization': `Bearer ${apiTokenRef.current}` } : {})
            }
          });
          const result = await res.json();
          if (result.success) {
            const baseUrl = API.replace('/api', '');
            const absoluteUrl = result.url.startsWith('/') ? baseUrl + result.url : result.url;
            const imageUrl = `![image](${absoluteUrl})\n`;
            setForm(prev => ({ ...prev, code: prev.code.replace(placeholder, imageUrl) }));
          } else {
            showNotification(result.error || 'Upload failed', 'error');
            setForm(prev => ({ ...prev, code: prev.code.replace(placeholder, '') }));
          }
        } catch (error) {
          console.error('Upload error:', error);
          showNotification('Upload failed', 'error');
          setForm(prev => ({ ...prev, code: prev.code.replace(placeholder, '') }));
        }
        break;
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = ''; // Reset input

    const placeholder = `![Uploading ${file.name}...]()\n`;
    setForm(prev => ({ ...prev, code: prev.code + (prev.code.endsWith('\n') || !prev.code ? '' : '\n') + placeholder }));

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(`${API}/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(apiTokenRef.current ? { 'Authorization': `Bearer ${apiTokenRef.current}` } : {})
        }
      });
      const result = await res.json();
      if (result.success) {
        const baseUrl = API.replace('/api', '');
        const absoluteUrl = result.url.startsWith('/') ? baseUrl + result.url : result.url;
        const imageUrl = `![image](${absoluteUrl})\n`;
        setForm(prev => ({ ...prev, code: prev.code.replace(placeholder, imageUrl) }));
      } else {
        showNotification(result.error || 'Upload failed', 'error');
        setForm(prev => ({ ...prev, code: prev.code.replace(placeholder, '') }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Upload failed', 'error');
      setForm(prev => ({ ...prev, code: prev.code.replace(placeholder, '') }));
    }
  };

  const saveSnippet = async () => {
    if (!form.title.trim() || !form.code.trim()) {
      showNotification('Title and code are required', 'info');
      return;
    }

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API}/snippets/${editingId}` : `${API}/snippets`;

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify({
          ...form,
          tags: form.tags.join(','),
          target_groups: form.visibility === 'private' ? 'All' : (form.target_groups.length > 0 ? form.target_groups.join(',') : 'All'),
          created_by: user.username,
          pinned: form.pinned
        })
      });

      if (res.ok) {
        setEditingId(null);
        setForm({
          title: '',
          description: '',
          code: '',
          language: 'javascript',
          tags: [],
          folder: 'General',
          visibility: 'public',
          target_groups: [],
          pinned: false
        });
        setView('snippets');
        refreshData();
        showNotification(`Snippet ${editingId ? 'updated' : 'created'} successfully`, 'success');
      } else {
        showNotification('Failed to save snippet', 'error');
      }
    } catch (error) {
      showNotification('Failed to save snippet. Please try again.', 'error');
    }
  };

  const deleteItem = async (type, id) => {
    const isSnippet = type === 'snippets';
    confirmAction(
      isSnippet ? 'Move to Recycle Bin' : `Delete ${type.slice(0, -1)}`,
      isSnippet
        ? 'This snippet will be moved to the recycle bin. You can restore it within 60 days.'
        : `Are you sure you want to delete this ${type.slice(0, -1)}? This action cannot be undone.`,
      async () => {
        try {
          const res = await fetch(`${API}/${type}/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
          });
          if (res.ok) {
            refreshData();
            showNotification(isSnippet ? 'Snippet moved to recycle bin' : `${type.slice(0, -1)} deleted`, 'success');
          } else {
            const error = await res.json();
            showNotification(error.error || 'Failed to delete', 'error');
          }
        } catch (error) {
          showNotification('Failed to delete. Please try again.', 'error');
        }
      }
    );
  };

  const handleCopy = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback method for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch (err) {
        showNotification('Failed to copy to clipboard', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setApiToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('apiToken');
    setView('snippets');
    setActiveFolder('All');
    // No need to show a notification here, the caller can do it.
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!userForm.username || !userForm.password) {
      showNotification('Username and password required', 'info');
      return;
    }
    try {
      const res = await fetch(`${API}/users`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...userForm,
          user_groups: userForm.user_groups.join(',')
        })
      });
      if (res.ok) {
        setUserForm({ username: '', password: '', role: 'tech', first_name: '', last_name: '', user_groups: [] });
        refreshData();
        showNotification('User created successfully!', 'success');
      } else {
        const error = await res.json();
        showNotification(error.error || 'Failed to create user', 'error');
      }
    } catch (error) {
      showNotification('Failed to create user', 'error');
    }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.password) {
      showNotification('New password required', 'info');
      return;
    }
    if (passwordForm.isSelfChange && !passwordForm.oldPassword) {
      showNotification('Old password required', 'info');
      return;
    }
    try {
      const res = await fetch(`${API}/users/${passwordForm.userId}/password`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          password: passwordForm.password,
          oldPassword: passwordForm.oldPassword,
          requestingUserId: user.id
        })
      });
      if (res.ok) {
        setPasswordForm({ userId: null, password: '', oldPassword: '', isSelfChange: false });
        showNotification('Password updated successfully!', 'success');
      } else {
        const error = await res.json();
        showNotification(error.error || 'Failed to update password', 'error');
      }
    } catch (error) {
      showNotification('Failed to update password', 'error');
    }
  };

  const startEditUser = (u) => {
    setEditingUser({
      id: u.id,
      role: u.role,
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      user_groups: u.user_groups ? u.user_groups.split(',') : []
    });
  };

  const updateUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          role: editingUser.role,
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          user_groups: editingUser.user_groups.join(',')
        })
      });
      if (res.ok) {
        setEditingUser(null);
        refreshData();
        showNotification('User updated successfully!', 'success');
      } else {
        showNotification('Failed to update user', 'error');
      }
    } catch (error) {
      showNotification('Failed to update user', 'error');
    }
  };

  const toggleGroupMembership = async (targetUser, groupName) => {
    try {
      const isMember = (targetUser.user_groups || '').split(',').includes(groupName);
      const newGroups = isMember
        ? targetUser.user_groups.split(',').filter(g => g !== groupName).join(',')
        : [...(targetUser.user_groups ? targetUser.user_groups.split(',') : []), groupName].join(',');

      const res = await fetch(`${API}/users/${targetUser.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          role: targetUser.role,
          first_name: targetUser.first_name,
          last_name: targetUser.last_name,
          user_groups: newGroups
        })
      });

      if (res.ok) {
        refreshData();
        showNotification(`${isMember ? 'Removed from' : 'Added to'} ${groupName}`, 'success');
      } else {
        const err = await res.json();
        showNotification(err.error || 'Failed to update membership', 'error');
      }
    } catch (error) {
      showNotification('Failed to toggle membership', 'error');
    }
  };

  const fetchVersions = async (snippetId) => {
    try {
      const res = await fetch(`${API}/snippets/${snippetId}/versions`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const versions = await res.json();
        setSnippetVersions({ snippetId, versions });
      } else {
        showNotification('Failed to fetch version history', 'error');
      }
    } catch (error) {
      showNotification('Error fetching versions', 'error');
    }
  };

  const restoreVersion = async (snippetId, versionId) => {
    try {
      const res = await fetch(`${API}/snippets/${snippetId}/restore/${versionId}`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        showNotification('Snippet restored to previous version', 'success');
        setSnippetVersions(null);
        refreshData();
      } else {
        const err = await res.json();
        showNotification(err.error || 'Failed to restore version', 'error');
      }
    } catch (error) {
      showNotification('Error restoring version', 'error');
    }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.name) {
      showNotification('Group name required', 'info');
      return;
    }
    try {
      const res = await fetch(`${API}/groups`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name: groupForm.name, created_by: user.username })
      });
      if (res.ok) {
        setGroupForm({ name: '' });
        refreshData();
        showNotification('Group created successfully!', 'success');
      } else {
        showNotification('Failed to create group', 'error');
      }
    } catch (error) {
      showNotification('Failed to create group', 'error');
    }
  };

  const createTag = async (e) => {
    e.preventDefault();
    if (!tagForm.name) {
      showNotification('Tag name required', 'info');
      return;
    }
    try {
      const res = await fetch(`${API}/tags`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(tagForm)
      });
      if (res.ok) {
        setTagForm({ name: '' });
        refreshData();
        showNotification('Tag created successfully!', 'success');
      } else {
        showNotification('Failed to create tag', 'error');
      }
    } catch (error) {
      showNotification('Failed to create tag', 'error');
    }
  };

  const createLanguage = async (e) => {
    e.preventDefault();
    if (!languageForm.name) {
      showNotification('Language name required', 'info');
      return;
    }
    try {
      const res = await fetch(`${API}/languages`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(languageForm)
      });
      if (res.ok) {
        setLanguageForm({ name: '' });
        refreshData();
        showNotification('Language added successfully!', 'success');
      } else {
        showNotification('Failed to add language', 'error');
      }
    } catch (error) {
      showNotification('Failed to add language', 'error');
    }
  };

  // Filter snippets based on permissions
  const filteredSnippets = data.snippets.filter(s => {
    // Permission checks
    const isOwner = s.created_by === user?.username;
    const isSuperuser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin';

    // Parse user groups (handle both old user_group and new user_groups)
    const userGroupsList = user?.user_groups ? user.user_groups.split(',') : [];

    // Parse snippet target groups (handle both old target_group and new target_groups)
    const snippetGroups = s.target_groups ? s.target_groups.split(',') : ['All'];
    const groupMatch = snippetGroups.includes('All') || snippetGroups.some(g => userGroupsList.includes(g));

    // Private snippets: Only owner or superuser can see
    if (s.visibility === 'private') {
      if (!isOwner && !isSuperuser) return false;
    }

    // Internal snippets: Only matching group members or superuser can see
    if (s.visibility === 'internal') {
      if (!groupMatch && !isSuperuser) return false;
    }

    // Public snippets: Everyone can see (no additional check needed)

    // Folder filter
    if (activeFolder !== "All" && (s.folder || 'General') !== activeFolder) return false;

    // Tag filter
    if (filterTag !== "All") {
      const snippetTags = s.tag ? s.tag.split(',').map(t => t.trim()) : [];
      if (!snippetTags.includes(filterTag)) return false;
    }

    // User filter
    if (filterUser !== "All" && s.created_by !== filterUser) return false;

    // Visibility filter
    if (filterVisibility !== "All" && s.visibility !== filterVisibility) return false;

    // Group filter
    if (filterGroup !== "All") {
      const snippetGroups = s.target_groups ? s.target_groups.split(',') : ['All'];
      if (!snippetGroups.includes(filterGroup)) return false;
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      const matchTitle = s.title.toLowerCase().includes(searchLower);
      const matchDesc = s.description && s.description.toLowerCase().includes(searchLower);
      const matchCode = searchFullCode && s.code && s.code.toLowerCase().includes(searchLower);

      if (!(matchTitle || matchDesc || matchCode)) return false;
    }

    return true;
  }).sort((a, b) => {
    // Pinned always first
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    // If custom order exists, use it
    if (customOrder.length > 0) {
      const aIdx = customOrder.indexOf(a.id);
      const bIdx = customOrder.indexOf(b.id);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
    }
    return b.id - a.id;
  });

  // Drag and drop handlers
  const handleDragStart = (e, snippetId) => {
    setDraggedId(snippetId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', snippetId);
  };

  const handleDragOver = (e, snippetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (snippetId !== dragOverId) setDragOverId(snippetId);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetId) {
      handleDragEnd();
      return;
    }
    // Build new order from current filteredSnippets
    const currentIds = filteredSnippets.map(s => s.id);
    const fromIndex = currentIds.indexOf(draggedId);
    const toIndex = currentIds.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) { handleDragEnd(); return; }
    const newOrder = [...currentIds];
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggedId);
    setCustomOrder(newOrder);
    localStorage.setItem('snippetOrder', JSON.stringify(newOrder));
    handleDragEnd();
  };

  // Touch-friendly move up/down for mobile
  const moveSnippet = (snippetId, direction) => {
    const currentIds = filteredSnippets.map(s => s.id);
    const currentIndex = currentIds.indexOf(snippetId);
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= currentIds.length) return;
    const newOrder = [...currentIds];
    [newOrder[currentIndex], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[currentIndex]];
    setCustomOrder(newOrder);
    localStorage.setItem('snippetOrder', JSON.stringify(newOrder));
  };

  // Get accessible snippets for folder calculation
  const accessibleSnippets = data.snippets.filter(s => {
    const isOwner = s.created_by === user?.username;
    const isSuperuser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin';

    const userGroupsList = user?.user_groups ? user.user_groups.split(',') : [];
    const snippetGroups = s.target_groups ? s.target_groups.split(',') : ['All'];
    const groupMatch = snippetGroups.includes('All') || snippetGroups.some(g => userGroupsList.includes(g));

    if (s.visibility === 'private') {
      if (!isOwner && !isSuperuser) return false;
    }
    if (s.visibility === 'internal') {
      if (!groupMatch && !isSuperuser) return false;
    }

    // If a specific group is selected in the sidebar/pills, only show folders from THAT group
    if (filterGroup !== "All") {
      if (!snippetGroups.includes(filterGroup)) return false;
    }

    return true;
  });

  const uniqueFolders = ['All', ...new Set(accessibleSnippets.map(s => s.folder || 'General'))];
  const availableFolders = uniqueFolders.filter(f => f !== 'All');

  // Build folder tree structure
  const buildFolderTree = () => {
    const tree = {};
    availableFolders.forEach(folder => {
      const parts = folder.split('/');
      let current = tree;
      parts.forEach((part, index) => {
        if (!current[part]) {
          current[part] = { _path: parts.slice(0, index + 1).join('/'), _children: {} };
        }
        current = current[part]._children;
      });
    });
    return tree;
  };

  const folderTree = buildFolderTree();

  // Folder tree item component
  // Folder tree item component with collapse/expand
  const FolderTreeItem = ({ tree, path, onSelect, theme, selectedPath, level = 0 }) => {
    return (
      <div style={{ marginLeft: level > 0 ? '12px' : '0' }}>
        {Object.keys(tree).map(key => {
          const item = tree[key];
          const currentPath = item._path;
          const hasChildren = Object.keys(item._children).length > 0;
          const isSelected = selectedPath === currentPath;
          const isChildSelected = selectedPath && selectedPath.startsWith(currentPath + '/');

          return (
            <FolderNode
              key={key}
              name={key}
              item={item}
              onSelect={onSelect}
              theme={theme}
              selectedPath={selectedPath}
              isSelected={isSelected}
              isChildSelected={isChildSelected}
              level={level}
              hasChildren={hasChildren}
            />
          );
        })}
      </div>
    );
  };


  const FolderNode = ({ name, item, onSelect, theme, selectedPath, isSelected, isChildSelected, level, hasChildren }) => {
    const [isOpen, setIsOpen] = useState(isChildSelected || isSelected);

    useEffect(() => {
      if (isChildSelected || isSelected) {
        setIsOpen(true);
      }
    }, [isChildSelected, isSelected]);

    return (
      <div style={{ marginBottom: '2px' }}>
        <div
          onClick={() => onSelect(item._path)}
          className="folder-item"
          style={{
            padding: '8px 10px',
            cursor: 'pointer',
            borderRadius: '8px',
            background: isSelected ? theme.accent + '15' : 'transparent',
            color: isSelected ? theme.accent : theme.text,
            fontWeight: isSelected || hasChildren ? '700' : '500',
            fontSize: '13.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            position: 'relative'
          }}
        >
          {hasChildren && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = themeMode === 'dark' ? '#ffffff10' : '#00000008'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
          )}
          {!hasChildren && <div style={{ width: '18px' }} />}

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {hasChildren ? (
              isOpen ? <FolderOpen size={16} color={isSelected ? theme.accent : theme.muted} /> : <Folder size={16} color={isSelected ? theme.accent : theme.muted} />
            ) : (
              <Hash size={14} style={{ opacity: 0.5 }} />
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          </div>
        </div>

        {hasChildren && isOpen && (
          <FolderTreeItem
            tree={item._children}
            path={[]}
            onSelect={onSelect}
            theme={theme}
            selectedPath={selectedPath}
            level={level + 1}
          />
        )}
      </div>
    );
  };

  // Public Snippet View (Overlay) (#15)
  // This must be rendered before the login check so unauthenticated users can see shared snippets
  if (publicSnippet) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: theme.bg, zIndex: 5000, padding: '40px 20px', overflowY: 'auto',
        display: 'flex', flexDirection: 'column', alignItems: 'center'
      }}>
        <div style={{ maxWidth: '1000px', width: '100%', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '40px',
            background: theme.card,
            padding: '20px 32px',
            borderRadius: '24px',
            border: `1px solid ${theme.border}`,
            boxShadow: theme.shadow
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accentHover} 100%)`,
                padding: '12px',
                borderRadius: '16px',
                display: 'flex',
                boxShadow: `0 8px 16px -4px ${theme.accent}40`
              }}>
                <Terminal size={28} color={getContrastColor(theme.accent)} />
              </div>
              <div>
                <h1 style={{ fontSize: '22px', fontWeight: '800', color: theme.text, letterSpacing: '-0.5px' }}>Snippet Vault <span style={{ color: theme.accent, fontWeight: '400', fontSize: '18px', margin: '0 8px' }}>/</span> <span style={{ color: theme.muted, fontWeight: '400', fontSize: '18px' }}>Shared View</span></h1>
                <p style={{ color: theme.muted, fontSize: '13px', fontWeight: '500' }}>Publicly accessible snippet</p>
              </div>
            </div>
            {user ? (
              <button onClick={() => setPublicSnippet(null)} className="btn btn-secondary" style={{ borderRadius: '12px', padding: '10px 20px' }}>
                <X size={18} /> Close View
              </button>
            ) : (
              <button onClick={() => window.location.href = '/'} className="btn btn-primary" style={{ borderRadius: '12px', padding: '10px 20px', boxShadow: `0 4px 12px ${theme.accent}30` }}>
                <LogOut size={18} /> Sign In to Vault
              </button>
            )}
          </div>

          <div className="snippet-card" style={{ background: theme.card, padding: '32px', borderRadius: '20px', border: `1px solid ${theme.border}`, boxShadow: theme.shadowLg }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '8px', color: theme.text }}>{publicSnippet.title}</h2>
              <p style={{ color: theme.muted, fontSize: '16px', marginBottom: '20px', lineHeight: '1.6' }}>{publicSnippet.description}</p>
              <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: theme.muted, fontWeight: '600', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Code size={16} /> {publicSnippet.language}
                </div>
                <span>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={16} /> Shared by {publicSnippet.created_by}
                </div>
                <span>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <RotateCcw size={16} /> {new Date(publicSnippet.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <pre className="syntax-highlight" style={{
                borderRadius: '16px',
                padding: '24px',
                background: themeMode === 'dark' ? '#0f172a' : '#1e293b',
                border: `1px solid ${theme.border}`,
                boxShadow: 'inset 0 2px 4px 0 rgba(0,0,0,0.1)',
                maxHeight: '600px',
                overflow: 'auto',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                <code className={`language-${publicSnippet.language.toLowerCase()}`} style={{ color: '#e2e8f0' }}>
                  {publicSnippet.code}
                </code>
              </pre>
            </div>

            <div style={{
              marginTop: '48px',
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              padding: '32px',
              background: themeMode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
              borderRadius: '28px',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.border}40`,
              boxShadow: themeMode === 'dark' ? '0 12px 40px rgba(0,0,0,0.3)' : '0 12px 40px rgba(0,0,0,0.05)'
            }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(publicSnippet.code);
                  setSharedCopied(true);
                  showNotification('Code copied to clipboard!', 'success');
                  setTimeout(() => setSharedCopied(false), 2000);
                }}
                className="btn btn-primary"
                style={{
                  padding: '18px 36px',
                  borderRadius: '18px',
                  boxShadow: `0 12px 30px -10px ${theme.accent}60`,
                  fontSize: '16px',
                  fontWeight: '700',
                  minWidth: '260px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  transform: sharedCopied ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                {sharedCopied ? <Check size={22} strokeWidth={3} /> : <Copy size={22} />}
                {sharedCopied ? 'Copied Success!' : 'Copy to Clipboard'}
              </button>

              <button
                onClick={() => window.location.href = '/'}
                className="btn btn-secondary"
                style={{
                  padding: '18px 36px',
                  borderRadius: '18px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: `2px solid ${theme.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  background: 'transparent',
                  color: theme.muted,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${theme.accent}10`;
                  e.currentTarget.style.borderColor = theme.accent;
                  e.currentTarget.style.color = theme.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.color = theme.muted;
                }}
              >
                <Sparkles size={20} /> Create Your Vault
              </button>
            </div>
          </div>

          <div style={{ marginTop: '40px', textAlign: 'center', color: theme.muted, fontSize: '14px' }}>
            <p>© {new Date().getFullYear()} Snippet Vault • Secure Code Management</p>
          </div>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: themeMode === 'cosmos' ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' : (themeMode === 'dark' ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'),
        padding: '20px'
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Space+Grotesk:wght@400;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', sans-serif; }
          h1, h2, h3 { font-family: 'Space Grotesk', sans-serif; letter-spacing: -0.02em; }
      `}</style>

        {notification && (() => {
          const notificationBg = notification.type === 'error' ? theme.danger : (notification.type === 'success' ? theme.success : theme.accent);
          return (
            <div style={{
              position: 'fixed', top: '24px', right: '24px',
              background: notificationBg,
              color: getContrastColor(notificationBg),
              padding: '12px 24px', borderRadius: '12px',
              boxShadow: theme.shadowLg, zIndex: 1200, display: 'flex', alignItems: 'center', gap: '10px',
              animation: 'slideIn 0.3s ease-out', fontWeight: '600', fontSize: '14px'
            }}>
              {notification.type === 'error' ? <X size={18} /> : (notification.type === 'success' ? <Check size={18} /> : <Terminal size={18} />)}
              {notification.message}
              <style>{`
                @keyframes slideIn {
                  from { transform: translateX(100%); opacity: 0; }
                  to { transform: translateX(0); opacity: 1; }
                }
              `}</style>
            </div>
          );
        })()}

        <div className="login-card" style={{
          background: theme.card,
          padding: '48px',
          borderRadius: '16px',
          boxShadow: theme.shadowLg,
          border: `1px solid ${theme.border} `,
          width: '100%',
          maxWidth: '420px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{
              background: theme.accent,
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Terminal color={getContrastColor(theme.accent)} size={32} />
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: theme.text, marginBottom: '8px', letterSpacing: '-0.5px' }}>
              Snippet Vault
            </h1>
            <p style={{ color: theme.muted, fontSize: '14px', fontWeight: '500' }}>
              Your team's code library
            </p>
          </div>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: theme.text }}>
                Username
              </label>
              <input
                type="text"
                value={auth.username}
                onChange={(e) => setAuth({ ...auth, username: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${theme.border} `,
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: theme.text,
                  background: theme.bg,
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = theme.accent}
                onBlur={(e) => e.target.style.borderColor = theme.border}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: theme.text }}>
                Password
              </label>
              <input
                type="password"
                value={auth.password}
                onChange={(e) => setAuth({ ...auth, password: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${theme.border} `,
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: theme.text,
                  background: theme.bg,
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="rememberMe"
                checked={auth.rememberMe}
                onChange={(e) => setAuth({ ...auth, rememberMe: e.target.checked })}
                style={{ marginRight: '8px', width: '16px', height: '16px', accentColor: theme.accent }}
              />
              <label htmlFor="rememberMe" style={{ color: theme.text, fontSize: '14px', fontWeight: '500', userSelect: 'none', cursor: 'pointer' }}>
                Remember Me
              </label>
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                background: theme.accent,
                color: getContrastColor(theme.accent),
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginTop: '8px'
              }}
              onMouseEnter={(e) => e.target.style.background = theme.accentHover}
              onMouseLeave={(e) => e.target.style.background = theme.accent}
            >
              Sign In
            </button>
          </form>

          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <button
              onClick={() => {
                const modes = ['light', 'dark', 'cosmos'];
                const currentIndex = modes.indexOf(themeMode);
                const nextIndex = (currentIndex + 1) % modes.length;
                setThemeMode(modes[nextIndex]);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: theme.muted,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
            >
              {themeMode === 'light' ? <Sun size={16} /> : (themeMode === 'dark' ? <Moon size={16} /> : <Sparkles size={16} color={theme.accent} />)}
              {themeMode === 'light' ? 'Light Mode' : (themeMode === 'dark' ? 'Dark Mode' : 'Cosmos Mode')}
            </button>
          </div>

          <div style={{ marginTop: '32px', padding: '16px', background: theme.bg, borderRadius: '10px', border: `1px solid ${theme.border} ` }}>
            <p style={{ fontSize: '12px', color: theme.muted, textAlign: 'center', fontWeight: '600' }}>
              Need help? Contact your administrator
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main App
  return (
    <div style={{ background: theme.bg, height: '100vh', color: theme.text, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* Background Decoration Elements */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '400px', overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        {/* Gradient wash — adapts per mode */}
        <div style={{
          position: 'absolute', inset: 0, background: themeMode === 'dark'
            ? `linear-gradient(135deg, ${theme.accent}15 0%, ${theme.accent}05 50%, transparent 100%)`
            : themeMode === 'cosmos'
              ? `linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(124,58,237,0.04) 50%, transparent 100%)`
              : `linear-gradient(135deg, rgba(99,102,241,0.05) 0%, transparent 100%)`
        }}></div>
        {/* Geometric prism SVG — visible in dark & cosmos */}
        {themeMode !== 'light' && (
          <div style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '50%', opacity: themeMode === 'dark' ? 0.25 : 0.15 }}>
            <svg style={{ height: '100%', width: '100%' }} fill="none" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
              <path d="M200 50L350 300H50L200 50Z" stroke="url(#prism-grad)" strokeWidth="0.5"></path>
              <path d="M200 50L350 300M350 300L200 350M200 350L50 300M50 300L200 50" stroke="url(#prism-grad)" strokeWidth="0.5"></path>
              <path d="M200 50V350" stroke="url(#prism-grad)" strokeWidth="0.5"></path>
              <defs>
                <linearGradient gradientUnits="userSpaceOnUse" id="prism-grad" x1="200" x2="200" y1="50" y2="350">
                  <stop stopColor={theme.accent}></stop>
                  <stop offset="1" stopColor={theme.accentHover} stopOpacity="0"></stop>
                </linearGradient>
              </defs>
            </svg>
          </div>
        )}
        {/* Light mode: subtle dot grid pattern overlay */}
        {themeMode === 'light' && (
          <div style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '60%', opacity: 0.4 }}>
            <svg style={{ width: '100%', height: '100%' }} xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
              <defs>
                <pattern id="dot-grid" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1" fill={theme.accent} fillOpacity="0.15" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dot-grid)" />
            </svg>
          </div>
        )}
        {/* Ambient glow */}
        <div style={{ position: 'absolute', bottom: '-20%', left: '-10%', width: '50%', height: '80%', background: `${theme.accent}${themeMode === 'dark' ? '08' : '06'}`, filter: 'blur(120px)', borderRadius: '50%' }}></div>
      </div>
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: theme.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Menu size={24} />
          </button>
          <span style={{ fontWeight: '800', fontSize: '16px', letterSpacing: '-0.5px' }}>
            VAULT
          </span>
        </div>
        <button
          onClick={() => {
            const modes = ['light', 'dark', 'cosmos'];
            const currentIndex = modes.indexOf(themeMode);
            const nextIndex = (currentIndex + 1) % modes.length;
            setThemeMode(modes[nextIndex]);
          }}
          className="btn btn-secondary"
          style={{ height: '36px', width: '36px', padding: 0, justifyContent: 'center' }}
        >
          {themeMode === 'light' ? <Sun size={18} /> : (themeMode === 'dark' ? <Moon size={18} /> : <Sparkles size={18} color={theme.accent} />)}
        </button>
      </div>

      <div style={{ display: 'flex', flex: 1, position: 'relative', zIndex: isSidebarOpen ? 1001 : 10, overflow: 'hidden' }}>
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Space+Grotesk:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Inter', sans-serif; 
          background: ${theme.bg};
          color: ${theme.text};
          line-height: 1.6;
        }
        
        h1, h2, h3, h4, .font-headline {
          font-family: 'Space Grotesk', sans-serif;
          letter-spacing: -0.02em;
        }

        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: ${theme.bg};
        }
        ::-webkit-scrollbar-thumb {
          background: ${theme.border};
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${theme.muted};
        }
        
        .snippet-card {
          background: ${theme.card};
          border: 1px solid ${theme.border};
          border-radius: 16px;
          padding: 24px;
          box-shadow: ${theme.shadow};
        }
        
        .snippet-card:hover {
          box-shadow: ${theme.shadowLg};
          border-color: ${theme.accent}60;
        }
        
        .input-field, .select-field, .textarea-field {
          width: 100%;
          padding: 12px 16px;
          background: ${themeMode === 'dark' ? '#131316' : theme.bg};
          border: 1px solid ${theme.border};
          color: ${theme.text};
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          font-family: 'JetBrains Mono', monospace;
          transition: all 0.2s;
          outline: none;
        }

        select option, .select-field option {
          background: ${themeMode === 'dark' ? '#19191d' : theme.card};
          color: ${theme.text};
        }
        
        .input-field:focus, .select-field:focus, .textarea-field:focus {
          border-color: ${theme.accent};
          box-shadow: 0 0 0 3px ${theme.accent}20;
        }

        .filter-select {
          height: 36px;
          padding: 0 30px 0 12px;
          border-radius: 8px;
          background: ${themeMode === 'dark' ? '#131316' : theme.card};
          border: 1px solid ${theme.border};
          color: ${theme.text};
          font-size: 12px;
          font-weight: 500;
          font-family: 'Inter', sans-serif;
          outline: none;
          cursor: pointer;
          white-space: nowrap;
        }

        .filter-select:hover {
          border-color: ${theme.accent}80;
          background: ${themeMode === 'dark' ? '#1a1a1f' : theme.bg};
        }

        .filter-select:focus {
          border-color: ${theme.accent};
          box-shadow: 0 0 0 3px ${theme.accent}20;
        }

        .filter-select option {
          background: ${themeMode === 'dark' ? '#19191d' : theme.card};
          color: ${theme.text};
          padding: 8px 12px;
        }

        .snippet-actions .btn {
          padding: 8px;
          min-width: 36px;
          height: 36px;
          justify-content: center;
        }
        
        .textarea-field {
          font-family: 'JetBrains Mono', monospace;
          line-height: 1.6;
          resize: vertical;
        }
        
        .btn {
          padding: 10px 18px;
          border-radius: 8px;
          border: none;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .btn:active {
          transform: scale(0.96);
        }
        
        .btn-primary {
          background: linear-gradient(135deg, ${theme.accent}, ${theme.accentHover});
          color: ${getContrastColor(theme.accent)};
          box-shadow: 0 4px 14px ${theme.accent}30;
        }
        
        .btn-primary:hover {
          filter: brightness(1.1);
          box-shadow: 0 6px 20px ${theme.accent}40;
        }
        
        .btn-secondary {
          background: ${themeMode === 'dark' ? '#25252a' : theme.bg};
          color: ${theme.text};
          border: 1px solid ${theme.border};
        }
        
        .btn-secondary:hover {
          background: ${themeMode === 'dark' ? '#2e2e33' : theme.card};
        }
        
        .btn-danger {
          background: transparent;
          color: ${theme.danger};
          border: 1px solid ${theme.border};
        }
        
        .btn-danger:hover {
          background: ${theme.danger}10;
        }
        
        .folder-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
        }
        
        .folder-item:hover {
          background: ${theme.bg};
        }

        code, pre {
          font-family: 'JetBrains Mono', monospace !important;
        }

        /* Syntax Highlighting */
        .token.comment { color: #6A9955; }
        .token.string { color: #CE9178; }
        .token.keyword { color: #569CD6; }
        .token.function { color: #DCDCAA; }
        .token.number { color: #B5CEA8; }
        .token.operator { color: #D4D4D4; }
        .token.property { color: #9CDCFE; }
        .token.punctuation { color: #D4D4D4; }
        .token.class-name { color: #4EC9B0; }
        .token.boolean { color: #569CD6; }
        .token.constant { color: #4FC1FF; }

        @media (max-width: 768px) {
          .sidebar-overlay {
            display: ${isSidebarOpen ? 'block' : 'none'} !important;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            backdrop-filter: blur(4px);
            z-index: 999;
          }

          aside {
            position: fixed !important;
            left: ${isSidebarOpen ? '0' : '-280px'};
            z-index: 1000;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: ${isSidebarOpen ? '20px 0 50px rgba(0,0,0,0.5)' : 'none'};
            width: 280px !important;
          }

          main {
            padding: 16px !important;
            margin-left: 0 !important;
          }

          .mobile-header {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px;
            background: ${theme.card};
            border-bottom: 1px solid ${theme.border};
            position: sticky;
            top: 0;
            z-index: 1002;
          }

          .create-grid {
            grid-template-columns: 1fr !important;
          }

          .login-card {
            padding: 24px 16px !important;
          }

          .snippet-card {
            padding: 12px !important;
          }

          .snippet-title {
            font-size: 15px !important;
          }

          .snippet-metadata {
            font-size: 11px !important;
          }

          .snippet-actions {
            gap: 4px !important;
            flex-wrap: wrap;
            justify-content: flex-start !important;
          }

          .snippet-actions .btn {
            padding: 6px !important;
            min-width: unset !important;
          }

          .snippet-actions .btn svg {
            width: 14px !important;
            height: 14px !important;
          }

          /* Hide drag handle on touch, show sort buttons instead */
          .drag-handle {
            display: none !important;
          }

          .touch-sort-controls {
            display: flex !important;
          }

          /* List view: stack rows vertically on mobile */
          .list-view-row {
            grid-template-columns: 1fr !important;
            gap: 4px !important;
            padding: 12px !important;
          }

          .list-view-header {
            display: none !important;
          }

          .list-view-col-hide {
            display: none !important;
          }

          .filter-select {
            flex: 1;
            min-width: 0;
            font-size: 11px !important;
          }

          pre {
            font-size: 11px !important;
            padding: 12px !important;
            line-height: 1.4 !important;
          }
        }

        @media (min-width: 769px) {
          .touch-sort-controls {
            display: none !important;
          }
        }

        .mobile-header {
          display: none;
        }

        .sidebar-overlay {
          display: none;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.8); }
        }
`}</style>

        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>

        {/* Sidebar */}
        <aside style={{
          width: '280px',
          minWidth: '280px',
          borderRight: `1px solid ${theme.border}`,
          background: theme.card,
          padding: '32px 20px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto'
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px' }}>
            <div style={{
              background: theme.accent,
              padding: '10px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Terminal color={getContrastColor(theme.accent)} size={20} />
            </div>
            <span style={{ fontWeight: '800', fontSize: '18px', letterSpacing: '-0.5px', lineHeight: '1.2' }}>
              TECH SUPPORT<br /><span style={{ color: theme.accent }}>VAULT</span>
            </span>
          </div>

          {/* Navigation */}
          <nav style={{ flex: 1 }}>
            <button
              onClick={() => { setView('snippets'); setEditingId(null); }}
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                background: view === 'snippets' ? `${theme.accent}15` : 'transparent',
                color: view === 'snippets' ? theme.accent : theme.muted,
                marginBottom: '8px'
              }}
            >
              <Folder size={18} /> Library
            </button>
            <button
              onClick={() => { setView('recycle-bin'); setEditingId(null); fetchRecycleBin(); }}
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                background: view === 'recycle-bin' ? `${theme.accent}15` : 'transparent',
                color: view === 'recycle-bin' ? theme.accent : theme.muted,
                marginBottom: '8px'
              }}
            >
              <Trash2 size={18} /> Recycle Bin
            </button>

            {(user?.role === 'admin' || user?.role === 'superuser') && (
              <>
                <button
                  onClick={() => { setView('users'); setEditingId(null); }}
                  className="btn"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    background: view === 'users' ? `${theme.accent}15` : 'transparent',
                    color: view === 'users' ? theme.accent : theme.muted,
                    marginBottom: '8px'
                  }}
                >
                  <Users size={18} /> Users
                </button>
                <button
                  onClick={() => { setView('groups'); setEditingId(null); }}
                  className="btn"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    background: view === 'groups' ? `${theme.accent}15` : 'transparent',
                    color: view === 'groups' ? theme.accent : theme.muted,
                    marginBottom: '8px'
                  }}
                >
                  <Shield size={18} /> Groups
                </button>
                <button
                  onClick={() => { setView('tags'); setEditingId(null); }}
                  className="btn"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    background: view === 'tags' ? `${theme.accent}15` : 'transparent',
                    color: view === 'tags' ? theme.accent : theme.muted,
                    marginBottom: '8px'
                  }}
                >
                  <TagIcon size={18} /> Tags
                </button>
                <button
                  onClick={() => { setView('languages'); setEditingId(null); }}
                  className="btn"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    background: view === 'languages' ? `${theme.accent}15` : 'transparent',
                    color: view === 'languages' ? theme.accent : theme.muted,
                    marginBottom: '8px'
                  }}
                >
                  <Code size={18} /> Languages
                </button>
                <button
                  onClick={() => { setView('stats'); setEditingId(null); }}
                  className="btn"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    background: view === 'stats' ? `${theme.accent}15` : 'transparent',
                    color: view === 'stats' ? theme.accent : theme.muted,
                    marginBottom: '8px'
                  }}
                >
                  <Sparkles size={18} /> Analytics
                </button>
                {user?.role === 'superuser' && (
                  <button
                    onClick={() => { setView('backups'); setEditingId(null); fetchBackups(); }}
                    className="btn"
                    style={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      background: view === 'backups' ? `${theme.accent}15` : 'transparent',
                      color: view === 'backups' ? theme.accent : theme.muted,
                      marginBottom: '8px'
                    }}
                  >
                    <HardDrive size={18} /> Backups
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => { setView('guide'); setEditingId(null); }}
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                background: view === 'guide' ? `${theme.accent}15` : 'transparent',
                color: view === 'guide' ? theme.accent : theme.muted,
                marginBottom: '8px'
              }}
            >
              <HelpCircle size={18} /> User Guide
            </button>

            {/* Teams / Group Pills */}
            <div style={{ marginTop: '32px' }}>
              <p style={{
                fontSize: '11px',
                fontWeight: '800',
                color: theme.muted,
                textTransform: 'uppercase',
                marginBottom: '12px',
                letterSpacing: '0.5px'
              }}>
                Teams
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                <button
                  onClick={() => setFilterGroup('All')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    border: `1px solid ${filterGroup === 'All' ? theme.accent : theme.border} `,
                    background: filterGroup === 'All' ? theme.accent : 'transparent',
                    color: filterGroup === 'All' ? getContrastColor(theme.accent) : theme.muted,
                    transition: 'all 0.2s',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}
                >
                  All
                </button>
                {data.groups.map(g => (
                  <button
                    key={g.id}
                    onClick={() => setFilterGroup(g.name)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      border: `1px solid ${filterGroup === g.name ? theme.accent : theme.border} `,
                      background: filterGroup === g.name ? theme.accent : 'transparent',
                      color: filterGroup === g.name ? getContrastColor(theme.accent) : theme.muted,
                      transition: 'all 0.2s',
                      fontFamily: 'JetBrains Mono, monospace'
                    }}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Folders */}
            <div style={{ marginTop: '16px' }}>
              <p style={{
                fontSize: '11px',
                fontWeight: '800',
                color: theme.muted,
                textTransform: 'uppercase',
                marginBottom: '12px',
                letterSpacing: '0.5px'
              }}>
                Folders
              </p>
              <div
                onClick={() => { setActiveFolder("All"); setView('snippets'); }}
                className="folder-item"
                style={{
                  color: activeFolder === "All" ? theme.accent : theme.text,
                  fontWeight: activeFolder === "All" ? '700' : '500',
                  background: activeFolder === "All" ? `${theme.accent}15` : 'transparent',
                  marginBottom: '8px',
                  border: activeFolder === "All" ? `1px solid ${theme.accent}30` : '1px solid transparent'
                }}
              >
                <Globe size={14} /> All Snippets
              </div>

              <FolderTreeItem
                tree={folderTree}
                path={[]}
                onSelect={(path) => { setActiveFolder(path); setView('snippets'); }}
                theme={theme}
                selectedPath={activeFolder}
              />
            </div>
          </nav>

          {/* Refresh Action Area (Timer hidden per user request) */}
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={refreshData}
              className="btn btn-secondary"
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '11px',
                borderRadius: '10px',
                justifyContent: 'flex-start',
                gap: '8px'
              }}
            >
              <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              <span style={{ fontWeight: '600', color: theme.muted }}>
                {loading ? 'Refreshing Snippets…' : 'Refresh Library'}
              </span>
            </button>
          </div>

          {/* User Info */}
          <div style={{ borderTop: `1px solid ${theme.border} `, paddingTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: `${theme.accent}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                color: theme.accent,
                fontSize: '16px'
              }}>
                {user.username[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: '700', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getUserDisplayName(user.username)}
                </p>
                <p style={{ color: theme.muted, fontSize: '12px', fontWeight: '500' }}>
                  {user.user_groups || 'General'} • {user.role}
                </p>
                {user.last_login && (
                  <p style={{ color: theme.muted, fontSize: '11px', fontWeight: '500', marginTop: '2px', opacity: 0.8 }} title="Last login time">
                    Last active: {new Date(user.last_login).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setPasswordForm({ userId: user.id, password: '', oldPassword: '', confirmPassword: '', isSelfChange: true });
                setShowPasswordModal(true);
              }}
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                color: theme.muted,
                background: 'transparent',
                fontSize: '13.5px',
                padding: '8px 16px',
                marginBottom: '4px'
              }}
            >
              <Lock size={16} /> Change Password
            </button>
            <button
              onClick={handleLogout}
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                color: theme.danger,
                background: 'transparent',
                fontSize: '13.5px',
                padding: '8px 16px'
              }}
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </aside>


        {/* Share Modal (#15) */}
        {
          shareModal && (
            <div className="modal-overlay" style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000
            }}>
              <div style={{
                background: theme.card, padding: '32px', borderRadius: '20px',
                width: '100%', maxWidth: '500px', border: `1px solid ${theme.border} `,
                boxShadow: theme.shadowLg
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '800' }}>Share Snippet</h3>
                  <button onClick={() => setShareModal(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: theme.muted }}>
                    <X size={20} />
                  </button>
                </div>
                <p style={{ color: theme.muted, fontSize: '14px', marginBottom: '20px' }}>
                  Anyone with this link can view this snippet without logging in.
                </p>
                <div style={{
                  display: 'flex', gap: '10px', padding: '12px', background: theme.bg,
                  borderRadius: '12px', border: `1px solid ${theme.border} `
                }}>
                  <input
                    readOnly
                    value={shareModal.share_url}
                    style={{ flex: 1, background: 'transparent', border: 'none', color: theme.text, fontSize: '13px', outline: 'none' }}
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(shareModal.share_url); showNotification('Link copied!', 'success'); }}
                    style={{ background: theme.accent, color: getContrastColor(theme.accent), border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
                  <button onClick={() => setShareModal(null)} className="btn btn-secondary" style={{ width: '100%' }}>Done</button>
                </div>
              </div>
            </div>
          )
        }

        {/* Main Content */}
        <main style={{ flex: 1, padding: displayMode === 'list' ? '24px 28px' : '24px 28px', overflowY: 'auto' }}>
          <div style={{ width: '100%' }}>
            {/* Header */}
            <div style={{ marginBottom: '28px' }}>
              {/* Row 1: Search + Filters */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{
                  position: 'relative',
                  flex: 1,
                  minWidth: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  background: themeMode === 'dark' ? '#131316' : theme.card,
                  borderRadius: '10px',
                  border: `1px solid ${theme.border}`,
                  padding: '2px 8px',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                }}>
                  <Search size={16} style={{ marginLeft: '6px', color: theme.muted }} />
                  <input
                    style={{
                      flex: 1,
                      background: 'transparent',
                      border: 'none',
                      padding: '7px 10px',
                      color: theme.text,
                      fontSize: '13px',
                      fontFamily: 'Inter, sans-serif',
                      outline: 'none'
                    }}
                    placeholder="Quick search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div style={{
                    background: themeMode === 'dark' ? '#25252a' : '#e2e8f0',
                    color: theme.muted,
                    fontSize: '10px',
                    padding: '3px 6px',
                    borderRadius: '5px',
                    fontFamily: 'JetBrains Mono, monospace',
                    fontWeight: '600',
                    marginRight: '4px'
                  }}>⌘K</div>
                </div>
                <select className="filter-select" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
                  <option value="All">All Tags</option>
                  {data.tags.map(tag => (
                    <option key={tag.id} value={tag.name}>{tag.name}</option>
                  ))}
                </select>
                <select className="filter-select" value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
                  <option value="All">All Users</option>
                  {data.users.map(u => (
                    <option key={u.id} value={u.username}>
                      {getUserDisplayName(u.username)}
                    </option>
                  ))}
                </select>
                <select className="filter-select" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                  <option value="All">All Groups</option>
                  {data.groups.map(g => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </select>
                <select className="filter-select" value={filterVisibility} onChange={(e) => setFilterVisibility(e.target.value)}>
                  <option value="All">Visibility: All</option>
                  <option value="public">🌐 Public</option>
                  <option value="internal">🏢 Internal</option>
                  <option value="private">🔒 Private</option>
                </select>
              </div>

              {/* Row 2: Actions */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* View toggle */}
                <div style={{
                  display: 'flex',
                  background: themeMode === 'dark' ? '#131316' : theme.bg,
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                  overflow: 'hidden'
                }}>
                  <button
                    onClick={() => { setDisplayMode('list'); localStorage.setItem('displayMode', 'list'); }}
                    title="List View"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: '36px', height: '36px',
                      background: displayMode === 'list' ? `${theme.accent}20` : 'transparent',
                      color: displayMode === 'list' ? theme.accent : theme.muted,
                      border: 'none', cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <List size={18} />
                  </button>
                  <button
                    onClick={() => { setDisplayMode('grid'); localStorage.setItem('displayMode', 'grid'); }}
                    title="Grid View"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: '36px', height: '36px',
                      background: displayMode === 'grid' ? `${theme.accent}20` : 'transparent',
                      color: displayMode === 'grid' ? theme.accent : theme.muted,
                      border: 'none', cursor: 'pointer',
                      borderLeft: `1px solid ${theme.border}`,
                      transition: 'all 0.2s'
                    }}
                  >
                    <LayoutGrid size={18} />
                  </button>
                </div>

                <button
                  className={`btn ${searchFullCode ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSearchFullCode(!searchFullCode)}
                  title="Toggle Full-Code Search"
                  style={{ padding: '0 14px', height: '36px', borderRadius: '8px', flexShrink: 0, fontSize: '13px' }}
                >
                  <Code size={16} />
                </button>

                {customOrder.length > 0 && (
                  <button
                    onClick={() => { setCustomOrder([]); localStorage.removeItem('snippetOrder'); }}
                    className="btn btn-secondary"
                    style={{ height: '36px', borderRadius: '8px', fontSize: '11px', padding: '0 10px', gap: '5px' }}
                    title="Reset to default sort order"
                  >
                    <RotateCcw size={13} /> Reset Order
                  </button>
                )}

                <div style={{ flex: 1 }} />

                <button
                  onClick={() => {
                    setEditingId(null);
                    setForm({
                      title: '',
                      description: '',
                      code: '',
                      language: 'javascript',
                      tags: [],
                      folder: 'General',
                      visibility: 'public',
                      target_groups: [],
                      pinned: false
                    });
                    setView('create');
                  }}
                  className="btn btn-primary"
                  style={{ height: '36px', padding: '0 18px', borderRadius: '8px', flexShrink: 0, fontSize: '13px' }}
                >
                  <Plus size={18} /> New Snippet
                </button>
                <button
                  onClick={() => {
                    const modes = ['light', 'dark', 'cosmos'];
                    const currentIndex = modes.indexOf(themeMode);
                    const nextIndex = (currentIndex + 1) % modes.length;
                    setThemeMode(modes[nextIndex]);
                  }}
                  className="btn btn-secondary"
                  style={{ height: '36px', width: '36px', padding: 0, justifyContent: 'center', borderRadius: '8px', flexShrink: 0 }}
                >
                  {themeMode === 'light' ? <Sun size={18} /> : (themeMode === 'dark' ? <Moon size={18} /> : <Sparkles size={18} color={theme.accent} />)}
                </button>
              </div>
            </div>

            {/* Create/Edit Modal */}
            {(view === 'create' || editingId) && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '24px'
              }}>
                <div style={{
                  background: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '16px',
                  width: '100%',
                  maxWidth: '900px',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: theme.shadowLg,
                  overflow: 'hidden'
                }}>
                  {/* Header bar */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 24px',
                    borderBottom: `1px solid ${theme.border}`,
                    background: themeMode === 'dark' ? '#131316' : theme.bg
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: theme.danger }}></span>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }}></span>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: theme.success }}></span>
                      </div>
                      <span style={{ fontSize: '14px', fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: theme.text }}>
                        {editingId ? 'Edit Snippet' : 'Create Snippet'}
                      </span>
                    </div>
                    <button
                      onClick={() => { setView('snippets'); setEditingId(null); }}
                      style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: '4px' }}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Scrollable form body */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                          Title *
                        </label>
                        <input
                          className="input-field"
                          value={form.title}
                          placeholder="e.g., React Custom Hook"
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />
                      </div>

                      <div className="create-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                            Language
                          </label>
                          <select
                            className="select-field"
                            value={form.language}
                            onChange={(e) => setForm({ ...form, language: e.target.value })}
                          >
                            {data.languages.map(lang => (
                              <option key={lang.id} value={lang.name.toLowerCase()}>{lang.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                            Tags (Multiple)
                          </label>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            padding: '10px',
                            background: themeMode === 'dark' ? '#131316' : theme.bg,
                            border: `1px solid ${theme.border}`,
                            borderRadius: '8px',
                            minHeight: '48px'
                          }}>
                            {data.tags.map(tag => (
                              <label key={tag.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                background: form.tags?.includes(tag.name) ? theme.accent : 'transparent',
                                color: form.tags?.includes(tag.name) ? '#ffffff' : theme.text,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                border: `1px solid ${form.tags?.includes(tag.name) ? theme.accent : theme.border}`,
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={form.tags?.includes(tag.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setForm({ ...form, tags: [...form.tags, tag.name] });
                                    } else {
                                      setForm({ ...form, tags: form.tags.filter(t => t !== tag.name) });
                                    }
                                  }}
                                  style={{ display: 'none' }}
                                />
                                #{tag.name}
                              </label>
                            ))}
                            {form.tags?.length === 0 && (
                              <span style={{ color: theme.muted, fontSize: '12px', padding: '4px' }}>
                                Select tags...
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ position: 'relative' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                            Folder
                          </label>
                          <div
                            className="input-field"
                            onClick={() => setShowFolderSelector(!showFolderSelector)}
                            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          >
                            <span>{form.folder || 'Select folder...'}</span>
                            <span>▼</span>
                          </div>

                          {showFolderSelector && (
                            <div style={{
                              position: 'absolute',
                              zIndex: 100,
                              background: theme.card,
                              border: `1px solid ${theme.border}`,
                              borderRadius: '10px',
                              marginTop: '4px',
                              padding: '12px',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              minWidth: '250px',
                              boxShadow: theme.shadowLg
                            }}>
                              <FolderTreeItem
                                tree={folderTree}
                                path={[]}
                                onSelect={(path) => {
                                  setForm({ ...form, folder: path });
                                  setShowFolderSelector(false);
                                }}
                                theme={theme}
                                selectedPath={form.folder}
                              />
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${theme.border}` }}>
                                <input
                                  className="input-field"
                                  placeholder="Or create new (e.g., Frontend/React)"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const value = e.target.value;
                                      const levels = value.split('/').filter(l => l.trim());
                                      if (levels.length <= 5) {
                                        setForm({ ...form, folder: value });
                                        setShowFolderSelector(false);
                                        e.target.value = '';
                                      } else {
                                        showNotification('Maximum 5 folder levels allowed', 'info');
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="create-grid" style={{ display: 'grid', gridTemplateColumns: form.visibility === 'private' ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                            Visibility
                          </label>
                          <select
                            className="select-field"
                            value={form.visibility}
                            onChange={(e) => {
                              const newVisibility = e.target.value;
                              setForm({
                                ...form,
                                visibility: newVisibility,
                                target_groups: newVisibility === 'private' ? [] : form.target_groups
                              });
                            }}
                          >
                            <option value="public">🌐 Public</option>
                            <option value="internal">🏢 Internal</option>
                            <option value="private">🔒 Private</option>
                          </select>
                        </div>
                        {form.visibility !== 'private' && form.visibility !== 'public' && (
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                              Target Groups (Multiple)
                            </label>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '8px',
                              padding: '10px',
                              background: themeMode === 'dark' ? '#131316' : theme.bg,
                              border: `1px solid ${theme.border}`,
                              borderRadius: '8px',
                              minHeight: '48px'
                            }}>
                              {data.groups.map(g => (
                                <label key={g.id} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '4px 10px',
                                  background: form.target_groups.includes(g.name) ? theme.accent : 'transparent',
                                  color: form.target_groups.includes(g.name) ? '#ffffff' : theme.text,
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  border: `1px solid ${form.target_groups.includes(g.name) ? theme.accent : theme.border}`,
                                  transition: 'all 0.2s'
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={form.target_groups.includes(g.name)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setForm({ ...form, target_groups: [...form.target_groups, g.name] });
                                      } else {
                                        setForm({ ...form, target_groups: form.target_groups.filter(gr => gr !== g.name) });
                                      }
                                    }}
                                    style={{ display: 'none' }}
                                  />
                                  {g.name}
                                </label>
                              ))}
                              {form.target_groups.length === 0 && (
                                <span style={{ color: theme.muted, fontSize: '12px', padding: '4px' }}>
                                  Select groups or leave empty for all
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>
                            Code * <span style={{ fontWeight: 'normal', color: theme.muted }}>(Paste or upload images)</span>
                          </label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <label className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '12px', cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '12px' }}>Upload Image</span>
                              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                            </label>
                            <button
                              type="button"
                              onClick={() => setEditorPreview(!editorPreview)}
                              className="btn btn-secondary"
                              style={{ padding: '4px 12px', fontSize: '12px' }}
                            >
                              {editorPreview ? 'Edit Code' : 'Preview'}
                            </button>
                          </div>
                        </div>
                        {editorPreview ? (
                          <pre style={{
                            minHeight: '300px', fontSize: '13px',
                            background: themeMode === 'dark' ? '#1a1a2e' : '#1e293b',
                            color: '#e2e8f0',
                            border: `1px solid ${theme.border}`,
                            borderRadius: '10px',
                            padding: '20px',
                            margin: 0,
                            overflowX: 'auto',
                            overflowY: 'auto'
                          }}>
                            <CodeWithImages code={form.code} themeMode={themeMode} />
                          </pre>
                        ) : (
                          <textarea
                            className="textarea-field"
                            value={form.code}
                            onPaste={handleImagePaste}
                            style={{ minHeight: '300px', fontSize: '13px', background: themeMode === 'dark' ? '#1a1a2e' : '#1e293b', color: '#e2e8f0', border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '20px' }}
                            placeholder="// Your code here..."
                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                          />
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="pinned"
                          checked={form.pinned}
                          onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                          style={{ cursor: 'pointer', accentColor: theme.accent, width: '16px', height: '16px' }}
                        />
                        <label htmlFor="pinned" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: theme.text }}>
                          Pin to top
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Footer bar */}
                  <div style={{
                    padding: '12px 24px',
                    borderTop: `1px solid ${theme.border}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: themeMode === 'dark' ? '#131316' : theme.bg
                  }}>
                    <span style={{ fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', color: theme.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {form.code ? form.code.split('\n').length : 0} lines • {form.language}
                    </span>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => { setView('snippets'); setEditingId(null); }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveSnippet}
                        className="btn btn-primary"
                      >
                        <Save size={18} />
                        {editingId ? 'Update' : 'Save'} Snippet
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Snippets List */}
            {view === 'snippets' && !editingId && (
              <div style={{
                ...(displayMode === 'list' ? {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                } : {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                  gap: '20px',
                  alignItems: 'start'
                })
              }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: theme.muted }}>
                    Loading...
                  </div>
                ) : filteredSnippets.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px' }}>
                    <p style={{ color: theme.muted, fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      No snippets found
                    </p>
                    <p style={{ color: theme.muted, fontSize: '14px', marginBottom: '20px' }}>
                      {search || filterTag !== 'All' || filterUser !== 'All' || filterVisibility !== 'All'
                        ? 'Try adjusting your filters or search query'
                        : 'Create your first snippet to get started'}
                    </p>
                    {!search && filterTag === 'All' && filterUser === 'All' && filterVisibility === 'All' && (
                      <button
                        onClick={() => setView('create')}
                        className="btn btn-primary"
                        style={{ margin: '0 auto' }}
                      >
                        <Plus size={18} /> Create Your First Snippet
                      </button>
                    )}
                  </div>
                ) : displayMode === 'list' ? (
                  /* ── LIST VIEW: Compact tabular rows ── */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
                    {/* Table header */}
                    <div className="list-view-header" style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr 100px 100px 160px 260px',
                      gap: '16px',
                      padding: '10px 16px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: theme.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: `2px solid ${theme.border}`,
                      userSelect: 'none'
                    }}>
                      <span style={{ width: '12px' }}></span>
                      <span>Title</span>
                      <span>Language</span>
                      <span>Visibility</span>
                      <span>Author</span>
                      <span style={{ textAlign: 'right' }}>Actions</span>
                    </div>
                    <AnimatePresence mode="popLayout">
                      {filteredSnippets.map(snippet => (
                        <motion.div
                          layout
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{
                            layout: { duration: 0.5, ease: "easeInOut" },
                            opacity: { duration: 0.15 },
                            x: { duration: 0.15 }
                          }}
                          key={snippet.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, snippet.id)}
                          onDragOver={(e) => handleDragOver(e, snippet.id)}
                          onDrop={(e) => handleDrop(e, snippet.id)}
                          onDragEnd={handleDragEnd}
                          className="list-view-row"
                          onClick={(e) => {
                            if (e.target.closest('button, a, input, textarea, select, [contenteditable]')) return;
                            handleCopy(snippet.code, snippet.id);
                          }}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr 100px 100px 160px 260px',
                            gap: '16px',
                            alignItems: 'center',
                            padding: '10px 16px',
                            background: theme.card,
                            borderRadius: '8px',
                            border: `1px solid ${dragOverId === snippet.id ? theme.accent : theme.border}`,
                            borderStyle: dragOverId === snippet.id ? 'dashed' : 'solid',
                            opacity: draggedId === snippet.id ? 0.4 : 1,
                            cursor: 'pointer',
                            transition: 'background 0.15s ease, border-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = themeMode === 'dark' ? '#1e1e23' : '#f8f7ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = theme.card; }}
                        >
                          {/* Drag handle (hidden on mobile via CSS) */}
                          <div className="drag-handle" style={{ color: theme.muted, opacity: 0.4, cursor: 'grab', flexShrink: 0 }} title="Drag to reorder">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.5" /><circle cx="11" cy="3" r="1.5" /><circle cx="5" cy="8" r="1.5" /><circle cx="11" cy="8" r="1.5" /><circle cx="5" cy="13" r="1.5" /><circle cx="11" cy="13" r="1.5" /></svg>
                          </div>
                          {/* Touch sort controls (shown on mobile via CSS) */}
                          <div className="touch-sort-controls" style={{ display: 'none', flexDirection: 'row', gap: '4px', flexShrink: 0 }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); moveSnippet(snippet.id, -1); }}
                              style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: '0', lineHeight: 1 }}
                              title="Move up"
                            >
                              <ChevronRight size={14} style={{ transform: 'rotate(-90deg)' }} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); moveSnippet(snippet.id, 1); }}
                              style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: '0', lineHeight: 1 }}
                              title="Move down"
                            >
                              <ChevronRight size={14} style={{ transform: 'rotate(90deg)' }} />
                            </button>
                          </div>
                          {/* Title + pin + folder + tags */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            {!!snippet.pinned && <Pin size={13} color={theme.accent} style={{ fill: theme.accent, flexShrink: 0 }} />}
                            <span
                              onClick={() => setPreviewSnippet(snippet)}
                              style={{
                                fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                                color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                transition: 'color 0.15s'
                              }}
                              onMouseEnter={(e) => e.target.style.color = theme.accent}
                              onMouseLeave={(e) => e.target.style.color = theme.text}
                            >
                              {snippet.title}
                            </span>
                            <span style={{ color: theme.muted, fontSize: '11px', fontWeight: '500', flexShrink: 0, opacity: 0.7 }}>
                              {snippet.folder !== 'General' ? snippet.folder : ''}
                            </span>
                            {snippet.tag && snippet.tag.split(',').map(t => t.trim()).filter(t => t).slice(0, 2).map((tag, i) => (
                              <span key={i} style={{
                                fontSize: '10px', fontWeight: '600', color: theme.accent,
                                background: `${theme.accent}12`, padding: '2px 6px', borderRadius: '4px', flexShrink: 0
                              }}>#{tag}</span>
                            ))}
                          </div>
                          {/* Language */}
                          <span className="list-view-col-hide" style={{
                            fontSize: '11px', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace',
                            color: theme.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {snippet.language}
                          </span>
                          {/* Visibility */}
                          <div className="list-view-col-hide" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {snippet.visibility === 'private' ? (
                              <Lock size={12} color={theme.muted} />
                            ) : snippet.visibility === 'internal' ? (
                              <Shield size={12} color={theme.muted} />
                            ) : (
                              <Globe size={12} color={theme.muted} />
                            )}
                            <span style={{ fontSize: '11px', color: theme.muted, textTransform: 'capitalize' }}>{snippet.visibility}</span>
                          </div>
                          {/* Author */}
                          <span className="list-view-col-hide" style={{
                            fontSize: '12px', fontWeight: '500', color: theme.muted,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {getUserDisplayName(snippet.created_by)}
                          </span>
                          {/* Actions */}
                          <div className="snippet-actions" style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end', flexShrink: 0 }}>
                            <button onClick={() => handleTogglePin(snippet.id)} className="btn btn-secondary" title={snippet.pinned ? "Unpin" : "Pin"}
                              style={{ padding: '5px', minWidth: '28px', height: '28px', background: snippet.pinned ? `${theme.accent}15` : 'transparent', color: snippet.pinned ? theme.accent : theme.muted, borderColor: snippet.pinned ? `${theme.accent}40` : theme.border }}>
                              <Pin size={13} style={{ fill: snippet.pinned ? theme.accent : 'none', transform: snippet.pinned ? 'none' : 'rotate(45deg)' }} />
                            </button>
                            {snippet.visibility === 'public' && (
                              <button onClick={() => handleShare(snippet.id)} className="btn btn-secondary" title="Share" style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                                <Share2 size={13} />
                              </button>
                            )}
                            <button onClick={() => fetchVersions(snippet.id)} className="btn btn-secondary" title="History" style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              <History size={13} />
                            </button>
                            <button onClick={() => startEdit(snippet)} className="btn btn-secondary" title="Edit" style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              <Edit3 size={13} />
                            </button>
                            <button onClick={() => duplicateSnippet(snippet)} className="btn btn-secondary" title="Duplicate" style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              <CopyPlus size={13} />
                            </button>
                            <button onClick={() => handleCopy(snippet.code, snippet.id)} className="btn btn-secondary" title="Copy" style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              {copiedId === snippet.id ? <Check size={13} color={theme.success} /> : <Copy size={13} />}
                            </button>
                            <button onClick={() => deleteItem('snippets', snippet.id)} className="btn btn-danger" title="Delete" style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                ) : (
                  /* ── GRID VIEW: Full cards with code preview ── */
                  <AnimatePresence mode="popLayout">
                    {filteredSnippets.map(snippet => (
                      <motion.div
                        layout
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        whileHover={{ y: -4, boxShadow: theme.shadowLg }}
                        transition={{
                          layout: { duration: 0.8, ease: "easeInOut" },
                          y: { duration: 0.2 },
                          boxShadow: { duration: 0.2 },
                          opacity: { duration: 0.2 }
                        }}
                        key={snippet.id}
                        className="snippet-card"
                        draggable
                        onDragStart={(e) => handleDragStart(e, snippet.id)}
                        onDragOver={(e) => handleDragOver(e, snippet.id)}
                        onDrop={(e) => handleDrop(e, snippet.id)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          if (e.target.closest('button, a, input, textarea, select, [contenteditable]')) return;
                          handleCopy(snippet.code, snippet.id);
                        }}
                        style={{
                          opacity: draggedId === snippet.id ? 0.4 : 1,
                          borderColor: dragOverId === snippet.id ? theme.accent : undefined,
                          borderStyle: dragOverId === snippet.id ? 'dashed' : undefined,
                          transition: 'opacity 0.2s, border-color 0.2s',
                          cursor: 'pointer',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'flex-start', gap: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            {/* Drag handle (hidden on mobile via CSS) */}
                            <div className="drag-handle" style={{ color: theme.muted, opacity: 0.4, cursor: 'grab', paddingTop: '2px', flexShrink: 0 }} title="Drag to reorder">
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.5" /><circle cx="11" cy="3" r="1.5" /><circle cx="5" cy="8" r="1.5" /><circle cx="11" cy="8" r="1.5" /><circle cx="5" cy="13" r="1.5" /><circle cx="11" cy="13" r="1.5" /></svg>
                            </div>
                            {/* Touch sort controls (shown on mobile via CSS) */}
                            <div className="touch-sort-controls" style={{ display: 'none', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); moveSnippet(snippet.id, -1); }}
                                style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: '0', lineHeight: 1 }}
                                title="Move up"
                              >
                                <ChevronRight size={14} style={{ transform: 'rotate(-90deg)' }} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); moveSnippet(snippet.id, 1); }}
                                style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: '0', lineHeight: 1 }}
                                title="Move down"
                              >
                                <ChevronRight size={14} style={{ transform: 'rotate(90deg)' }} />
                              </button>
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                <h3 className="snippet-title" style={{ fontSize: '15px', fontWeight: '700', cursor: 'pointer' }} onClick={() => setPreviewSnippet(snippet)}>
                                  {snippet.title}
                                </h3>
                                {snippet.visibility === 'private' ? (
                                  <Lock size={12} color={theme.muted} />
                                ) : snippet.visibility === 'internal' ? (
                                  <Shield size={12} color={theme.muted} />
                                ) : (
                                  <Globe size={12} color={theme.muted} />
                                )}
                              </div>
                              <p className="snippet-metadata" style={{ color: theme.muted, fontSize: '11px', fontWeight: '500' }}>
                                {snippet.folder} • {snippet.language} • by {getUserDisplayName(snippet.created_by)}
                                {snippet.last_edited_by && snippet.last_edited_by !== snippet.created_by && ` • edited by ${getUserDisplayName(snippet.last_edited_by)} `}
                                {snippet.tag && snippet.tag.split(',').map(t => t.trim()).filter(t => t).map((tag, i) => (
                                  <span key={i}> • #{tag}</span>
                                ))}
                              </p>
                            </div>
                          </div>
                          <div className="snippet-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleTogglePin(snippet.id)}
                              className="btn btn-secondary"
                              title={snippet.pinned ? "Unpin" : "Pin to top"}
                              style={{
                                background: snippet.pinned ? `${theme.accent}15` : 'transparent',
                                color: snippet.pinned ? theme.accent : theme.muted,
                                borderColor: snippet.pinned ? `${theme.accent}40` : theme.border,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Pin size={16} style={{
                                fill: snippet.pinned ? theme.accent : 'none',
                                transform: snippet.pinned ? 'none' : 'rotate(45deg)',
                                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                              }} />
                            </button>
                            {snippet.visibility === 'public' && (
                              <button
                                onClick={() => handleShare(snippet.id)}
                                className="btn btn-secondary"
                                title="Share Public Link"
                              >
                                <Share2 size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => fetchVersions(snippet.id)}
                              className="btn btn-secondary"
                              title="Version History"
                            >
                              <History size={16} />
                            </button>
                            <button
                              onClick={() => startEdit(snippet)}
                              className="btn btn-secondary"
                              title="Edit"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => duplicateSnippet(snippet)}
                              className="btn btn-secondary"
                              title="Duplicate"
                            >
                              <CopyPlus size={16} />
                            </button>
                            <button
                              onClick={() => handleCopy(snippet.code, snippet.id)}
                              className="btn btn-secondary"
                            >
                              {copiedId === snippet.id ? (
                                <Check size={16} color={theme.success} />
                              ) : (
                                <Copy size={16} />
                              )}
                            </button>
                            <button
                              onClick={() => deleteItem('snippets', snippet.id)}
                              className="btn btn-danger"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        <div style={{ position: 'relative' }}>
                          <pre style={{
                            borderRadius: '10px',
                            padding: '16px',
                            margin: 0,
                            background: themeMode === 'dark' ? '#1a1a2e' : '#1e293b',
                            overflowX: 'auto',
                            overflowY: 'auto',
                            maxHeight: '400px',
                            border: `1px solid ${theme.border}`,
                            fontSize: '12px',
                            lineHeight: '1.6',
                            color: '#e2e8f0',
                            fontFamily: 'JetBrains Mono, monospace'
                          }}>
                            <CodeWithImages code={snippet.code} themeMode={themeMode} />
                          </pre>
                          {/* Fade-out gradient for long code */}
                          {snippet.code && snippet.code.split('\n').length > 16 && (
                            <div style={{
                              position: 'absolute', bottom: 0, left: 0, right: 0, height: '60px',
                              background: `linear-gradient(transparent, ${themeMode === 'dark' ? '#1a1a2e' : '#1e293b'})`,
                              borderRadius: '0 0 10px 10px', pointerEvents: 'none'
                            }}></div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
                {view === 'snippets' && hasMore && !loading && (
                  <button
                    onClick={() => refreshData(true)}
                    className="btn btn-secondary"
                    style={{ width: '100%', padding: '12px', marginTop: '10px', gridColumn: '1 / -1' }}
                  >
                    <RefreshCw size={18} /> Load More Snippets
                  </button>
                )}
              </div>
            )}

            {/* Stats View (#22) */}
            {view === 'stats' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div className="stats-card" style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} `, boxShadow: theme.shadow }}>
                    <p style={{ color: theme.muted, fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Total Snippets</p>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', color: theme.text }}>{stats?.totalSnippets || 0}</h2>
                  </div>
                  <div className="stats-card" style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} `, boxShadow: theme.shadow }}>
                    <p style={{ color: theme.muted, fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Total Users</p>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', color: theme.text }}>{stats?.totalUsers || 0}</h2>
                  </div>
                  <div className="stats-card" style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} `, boxShadow: theme.shadow }}>
                    <p style={{ color: theme.muted, fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Comments</p>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', color: theme.text }}>{stats?.totalComments || 0}</h2>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                  <div style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} ` }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700' }}>Recent Activity</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {stats?.recentActivity?.map((act, i) => (
                        <div key={i} style={{ fontSize: '13px', paddingBottom: '8px', borderBottom: `1px solid ${theme.border} ` }}>
                          <span style={{ fontWeight: '700' }}>{act.performed_by}</span> {act.action.toLowerCase()}
                          <span style={{ color: theme.accent }}> {act.resource_title || act.resource_type}</span>
                          <p style={{ color: theme.muted, fontSize: '11px' }}>{new Date(act.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} ` }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700' }}>Languages</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {stats?.snippetsByLanguage?.map((l, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span>{l.language}</span>
                          <span style={{ fontWeight: '700' }}>{l.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} ` }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700' }}>Visibility</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {stats?.snippetsByVisibility?.map((v, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span style={{ textTransform: 'capitalize' }}>{v.visibility}</span>
                          <span style={{ fontWeight: '700' }}>{v.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} ` }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700' }}>Top Contributors</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {stats?.snippetsByUser?.map((u, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span>{getUserDisplayName(u.created_by)}</span>
                          <span style={{ fontWeight: '700' }}>{u.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: theme.card, padding: '24px', borderRadius: '16px', border: `1px solid ${theme.border} ` }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700' }}>Recent Snippets</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {stats?.recentSnippets?.map((s, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span style={{
                            maxWidth: '180px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: '600'
                          }}>{s.title}</span>
                          <span style={{ color: theme.muted, fontSize: '12px' }}>{s.created_by}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* User Management */}
            {view === 'users' && (user?.role === 'admin' || user?.role === 'superuser') && (
              <div>
                <div className="snippet-card" style={{ marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>Create New User</h2>
                  <form onSubmit={createUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <input
                        className="input-field"
                        value={userForm.username}
                        placeholder="Username *"
                        onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                        required
                      />
                      <input
                        className="input-field"
                        type="password"
                        value={userForm.password}
                        placeholder="Password *"
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <input
                        className="input-field"
                        value={userForm.first_name}
                        placeholder="First Name"
                        onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                      />
                      <input
                        className="input-field"
                        value={userForm.last_name}
                        placeholder="Last Name"
                        onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                        Role
                      </label>
                      <select
                        className="select-field"
                        value={userForm.role}
                        onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      >
                        <option value="tech">Tech (Viewer)</option>
                        <option value="admin">Admin (Manager)</option>
                        {user?.role === 'superuser' && <option value="superuser">Superuser (Full Access)</option>}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                        Groups (Multiple)
                      </label>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        padding: '10px',
                        background: theme.bg,
                        border: `2px solid ${theme.border} `,
                        borderRadius: '10px',
                        minHeight: '48px'
                      }}>
                        {data.groups.map(g => {
                          const canManageGroup = user?.role === 'superuser' || g.created_by === user?.username;
                          const isSelected = userForm.user_groups.includes(g.name);
                          return (
                            <label key={g.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: isSelected ? theme.accent : theme.card,
                              color: isSelected ? '#ffffff' : theme.text,
                              borderRadius: '6px',
                              cursor: canManageGroup ? 'pointer' : 'not-allowed',
                              fontSize: '13px',
                              fontWeight: '600',
                              border: `1px solid ${isSelected ? theme.accent : theme.border} `,
                              transition: 'all 0.2s',
                              opacity: canManageGroup ? 1 : 0.6,
                              position: 'relative'
                            }} title={canManageGroup ? "" : "Managed by creator only"}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={!canManageGroup}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setUserForm({ ...userForm, user_groups: [...userForm.user_groups, g.name] });
                                  } else {
                                    setUserForm({ ...userForm, user_groups: userForm.user_groups.filter(gr => gr !== g.name) });
                                  }
                                }}
                                style={{ display: 'none' }}
                              />
                              {!canManageGroup && <Lock size={12} style={{ marginRight: '2px' }} />}
                              {g.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary">
                      <Plus size={18} /> Create User
                    </button>
                  </form>
                </div>

                <div className="snippet-card">
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>All Users</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {data.users.map(u => (
                      <div key={u.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        background: theme.bg,
                        borderRadius: '8px',
                        border: `1px solid ${theme.border} `
                      }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: '700', fontSize: '15px' }}>
                            {u.first_name && u.last_name ? `${u.first_name} ${u.last_name} ` : u.username}
                          </p>
                          <p style={{ color: theme.muted, fontSize: '13px' }}>
                            @{u.username} • {u.role} • {u.user_groups || 'No groups'}
                          </p>
                        </div>
                        <div className="snippet-actions" style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => startEditUser(u)}
                            className="btn btn-secondary"
                            style={{ fontSize: '12px', padding: '8px 12px' }}
                          >
                            <Edit size={14} /> Edit
                          </button>
                          <button
                            onClick={() => setPasswordForm({ userId: u.id, password: '', oldPassword: '', isSelfChange: false })}
                            className="btn btn-secondary"
                            style={{ fontSize: '12px', padding: '8px 12px' }}
                          >
                            Reset Password
                          </button>
                          {u.username !== 'admin' && user?.role === 'superuser' && (
                            <button
                              onClick={() => deleteItem('users', u.id)}
                              className="btn btn-danger"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Change Password Modal */}
                {passwordForm.userId && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div className="snippet-card" style={{ maxWidth: '400px', width: '90%' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '16px' }}>
                        {passwordForm.isSelfChange ? 'Change Your Password' : 'Reset User Password'}
                      </h3>
                      <form onSubmit={updatePassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {passwordForm.isSelfChange && (
                          <input
                            className="input-field"
                            type="password"
                            value={passwordForm.oldPassword}
                            placeholder="Current Password"
                            onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                            required
                            autoFocus
                          />
                        )}
                        <input
                          className="input-field"
                          type="password"
                          value={passwordForm.password}
                          placeholder="New Password"
                          onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                          required
                          autoFocus={!passwordForm.isSelfChange}
                        />
                        {passwordForm.isSelfChange && (
                          <p style={{ fontSize: '12px', color: theme.muted, margin: 0 }}>
                            Enter your current password to confirm the change
                          </p>
                        )}
                        {!passwordForm.isSelfChange && user?.role !== 'superuser' && (
                          <p style={{ fontSize: '12px', color: theme.danger, margin: 0 }}>
                            Only superusers can reset passwords. Please contact your administrator.
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => setPasswordForm({ userId: null, password: '', oldPassword: '', isSelfChange: false })}
                            className="btn btn-secondary"
                            style={{ flex: 1 }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ flex: 1 }}
                            disabled={!passwordForm.isSelfChange && user?.role !== 'superuser'}
                          >
                            Update Password
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Edit User Modal */}
                {editingUser && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div className="snippet-card" style={{ maxWidth: '500px', width: '90%' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '16px' }}>Edit User</h3>
                      <form onSubmit={updateUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <input
                            className="input-field"
                            value={editingUser.first_name}
                            placeholder="First Name"
                            onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
                          />
                          <input
                            className="input-field"
                            value={editingUser.last_name}
                            placeholder="Last Name"
                            onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                            Role
                          </label>
                          <select
                            className="select-field"
                            value={editingUser.role}
                            onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          >
                            <option value="tech">Tech (Viewer)</option>
                            <option value="admin">Admin (Manager)</option>
                            {user?.role === 'superuser' && <option value="superuser">Superuser (Full Access)</option>}
                          </select>
                        </div>
                        {data.users.find(u => u.id === editingUser.id)?.username !== 'admin' && (
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                              Groups (Multiple)
                            </label>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '8px',
                              padding: '10px',
                              background: theme.bg,
                              border: `2px solid ${theme.border} `,
                              borderRadius: '10px',
                              minHeight: '48px'
                            }}>
                              {data.groups.map(g => {
                                const canManageGroup = user?.role === 'superuser' || g.created_by === user?.username;
                                const isSelected = editingUser.user_groups.includes(g.name);
                                return (
                                  <label key={g.id} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    background: isSelected ? theme.accent : theme.card,
                                    color: isSelected ? '#ffffff' : theme.text,
                                    borderRadius: '6px',
                                    cursor: canManageGroup ? 'pointer' : 'not-allowed',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    border: `1px solid ${isSelected ? theme.accent : theme.border} `,
                                    transition: 'all 0.2s',
                                    opacity: canManageGroup ? 1 : 0.6
                                  }} title={canManageGroup ? "" : "Managed by creator only"}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={!canManageGroup}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setEditingUser({ ...editingUser, user_groups: [...editingUser.user_groups, g.name] });
                                        } else {
                                          setEditingUser({ ...editingUser, user_groups: editingUser.user_groups.filter(gr => gr !== g.name) });
                                        }
                                      }}
                                      style={{ display: 'none' }}
                                    />
                                    {!canManageGroup && <Lock size={12} style={{ marginRight: '2px' }} />}
                                    {g.name}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {data.users.find(u => u.id === editingUser.id)?.username === 'admin' && (
                          <div style={{ padding: '12px', background: theme.bg, borderRadius: '8px', border: `1px solid ${theme.border} ` }}>
                            <p style={{ fontSize: '13px', color: theme.muted, margin: 0 }}>
                              ⚠️ Admin user's groups cannot be modified
                            </p>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => setEditingUser(null)}
                            className="btn btn-secondary"
                            style={{ flex: 1 }}
                          >
                            Cancel
                          </button>
                          <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                            Save Changes
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Group Management */}
            {view === 'groups' && (user?.role === 'admin' || user?.role === 'superuser') && (
              <div>
                <div className="snippet-card" style={{ marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>Create New Group</h2>
                  <form onSubmit={createGroup} style={{ display: 'flex', gap: '12px' }}>
                    <input
                      className="input-field"
                      value={groupForm.name}
                      placeholder="Group name (e.g., Frontend)"
                      onChange={(e) => setGroupForm({ name: e.target.value })}
                      required
                      style={{ flex: 1 }}
                    />
                    <button type="submit" className="btn btn-primary">
                      <Plus size={18} /> Create
                    </button>
                  </form>
                </div>

                <div className="snippet-card">
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>All Groups</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                    {data.groups.map(g => (
                      <div key={g.id} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        padding: '20px',
                        background: theme.bg,
                        borderRadius: '12px',
                        border: `1px solid ${theme.border} `,
                        transition: 'all 0.2s'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            background: `${theme.accent}15`,
                            padding: '8px',
                            borderRadius: '8px',
                            display: 'flex'
                          }}>
                            <Shield size={20} color={theme.accent} />
                          </div>
                          <span style={{ fontWeight: '700', fontSize: '16px', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {g.name}
                          </span>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
                          {(user?.role === 'superuser' || g.created_by === user?.username) ? (
                            <button
                              onClick={() => setManagingGroup(g)}
                              className="btn btn-secondary"
                              style={{ flex: 1, padding: '8px', fontSize: '13px' }}
                            >
                              <Users size={16} /> Members
                            </button>
                          ) : (
                            <div style={{
                              flex: 1,
                              padding: '8px',
                              background: `${theme.muted}10`,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              opacity: 0.6,
                              border: `1px dashed ${theme.border} `
                            }} title="Managed by creator only">
                              <Lock size={14} color={theme.muted} />
                              <span style={{ fontSize: '12px', fontWeight: '600', color: theme.muted }}>Locked</span>
                            </div>
                          )}
                          {(user?.role === 'superuser' || g.created_by === user?.username) && (
                            <button
                              onClick={() => deleteItem('groups', g.id)}
                              className="btn btn-danger"
                              style={{ padding: '8px', width: '40px' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                          {(user?.role !== 'superuser' && g.created_by !== user?.username) && (
                            <div style={{ padding: '8px', opacity: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px' }} title="Only creators can delete">
                              <Lock size={16} color={theme.muted} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Group Members Modal */}
            {managingGroup && (
              <div style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div className="snippet-card" style={{ maxWidth: '600px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '800', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Manage Members: {managingGroup.name}
                    </h3>
                    <button onClick={() => setManagingGroup(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.text }}>
                      <X size={20} />
                    </button>
                  </div>

                  <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '8px' }}>
                    {data.users.map(u => {
                      const isMember = (u.user_groups || '').split(',').includes(managingGroup.name);
                      return (
                        <div key={u.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          background: theme.bg,
                          borderRadius: '12px',
                          border: `1px solid ${theme.border} `,
                          gap: '12px'
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontWeight: '700', fontSize: '14px', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getUserDisplayName(u.username)}
                            </p>
                            <p style={{ fontSize: '12px', color: theme.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              @{u.username}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleGroupMembership(u, managingGroup.name)}
                            className={`btn ${isMember ? 'btn-danger' : 'btn-primary'} `}
                            style={{ padding: '6px 16px', fontSize: '12px', minWidth: '90px', flexShrink: 0 }}
                          >
                            {isMember ? 'Remove' : 'Add'}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setManagingGroup(null)}
                    className="btn btn-secondary"
                    style={{ marginTop: '20px', width: '100%' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Tag Management */}
            {view === 'tags' && (user?.role === 'admin' || user?.role === 'superuser') && (
              <div>
                <div className="snippet-card" style={{ marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>Create New Tag</h2>
                  <form onSubmit={createTag} style={{ display: 'flex', gap: '12px' }}>
                    <input
                      className="input-field"
                      value={tagForm.name}
                      placeholder="Tag name (e.g., react, hooks)"
                      onChange={(e) => setTagForm({ name: e.target.value })}
                      required
                      style={{ flex: 1 }}
                    />
                    <button type="submit" className="btn btn-primary">
                      <Plus size={18} /> Create
                    </button>
                  </form>
                </div>

                <div className="snippet-card">
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>All Tags</h2>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {data.tags.map(t => (
                      <div key={t.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: theme.bg,
                        borderRadius: '6px',
                        border: `1px solid ${theme.border} `
                      }}>
                        <Hash size={14} color={theme.accent} />
                        <span style={{ fontWeight: '600', fontSize: '13px' }}>{t.name}</span>
                        <button
                          onClick={() => deleteItem('tags', t.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: theme.danger,
                            cursor: 'pointer',
                            padding: '2px',
                            display: 'flex'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Language Management */}
            {view === 'languages' && (user?.role === 'admin' || user?.role === 'superuser') && (
              <div>
                <div className="snippet-card" style={{ marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>Add New Language</h2>
                  <form onSubmit={createLanguage} style={{ display: 'flex', gap: '12px' }}>
                    <input
                      className="input-field"
                      value={languageForm.name}
                      placeholder="Language name (e.g., Elixir, Haskell)"
                      onChange={(e) => setLanguageForm({ name: e.target.value })}
                      required
                      style={{ flex: 1 }}
                    />
                    <button type="submit" className="btn btn-primary">
                      <Plus size={18} /> Add
                    </button>
                  </form>
                </div>

                <div className="snippet-card">
                  <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px' }}>All Languages</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                    {data.languages.map(lang => (
                      <div key={lang.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        background: theme.bg,
                        borderRadius: '6px',
                        border: `1px solid ${theme.border} `
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Code size={14} color={theme.accent} />
                          <span style={{ fontWeight: '600', fontSize: '13px', fontFamily: 'JetBrains Mono' }}>{lang.name}</span>
                        </div>
                        <button
                          onClick={() => deleteItem('languages', lang.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: theme.danger,
                            cursor: 'pointer',
                            padding: '2px',
                            display: 'flex'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {/* User Guide View */}
            {view === 'guide' && <UserGuide theme={theme} themeMode={themeMode} />}

            {/* Recycle Bin View */}
            {view === 'recycle-bin' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h2 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '4px' }}>Recycle Bin</h2>
                    <p style={{ color: theme.muted, fontSize: '13px' }}>Deleted snippets are kept for 60 days before permanent removal.</p>
                  </div>
                  {user?.role === 'superuser' && recycleBin.length > 0 && (
                    <button onClick={emptyRecycleBin} className="btn btn-danger" style={{ flexShrink: 0 }}>
                      <Trash2 size={16} /> Empty Recycle Bin
                    </button>
                  )}
                </div>

                {recycleBinLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: theme.muted }}>Loading...</div>
                ) : recycleBin.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px' }}>
                    <Trash2 size={48} color={theme.muted} style={{ marginBottom: '16px', opacity: 0.3 }} />
                    <p style={{ color: theme.muted, fontSize: '16px', fontWeight: '600' }}>Recycle bin is empty</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div className="list-view-header" style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 100px 120px 100px 140px',
                      gap: '16px',
                      padding: '10px 16px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: theme.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: `2px solid ${theme.border}`,
                      userSelect: 'none'
                    }}>
                      <span>Title</span>
                      <span>Language</span>
                      <span>Deleted By</span>
                      <span>Days Left</span>
                      <span style={{ textAlign: 'right' }}>Actions</span>
                    </div>
                    <AnimatePresence mode="popLayout">
                      {recycleBin.map(snippet => (
                        <motion.div
                          layout
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ layout: { duration: 0.3 }, opacity: { duration: 0.15 } }}
                          key={snippet.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 100px 120px 100px 140px',
                            gap: '16px',
                            alignItems: 'center',
                            padding: '10px 16px',
                            background: theme.card,
                            borderRadius: '8px',
                            border: `1px solid ${theme.border}`,
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = themeMode === 'dark' ? '#1e1e23' : '#f8f7ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = theme.card; }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <span
                              onClick={() => setPreviewSnippet(snippet)}
                              style={{
                                fontWeight: '600', fontSize: '14px', color: theme.text, cursor: 'pointer',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                transition: 'color 0.15s'
                              }}
                              onMouseEnter={(e) => e.target.style.color = theme.accent}
                              onMouseLeave={(e) => e.target.style.color = theme.text}
                            >
                              {snippet.title}
                            </span>
                            {snippet.tag && snippet.tag.split(',').map(t => t.trim()).filter(t => t).slice(0, 2).map((tag, i) => (
                              <span key={i} style={{
                                fontSize: '10px', fontWeight: '600', color: theme.accent,
                                background: `${theme.accent}12`, padding: '2px 6px', borderRadius: '4px', flexShrink: 0
                              }}>#{tag}</span>
                            ))}
                          </div>
                          <span className="list-view-col-hide" style={{
                            fontSize: '11px', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace',
                            color: theme.muted
                          }}>
                            {snippet.language}
                          </span>
                          <span className="list-view-col-hide" style={{ fontSize: '12px', color: theme.muted }}>
                            {snippet.deleted_by || '—'}
                          </span>
                          <span style={{
                            fontSize: '12px', fontWeight: '600',
                            color: snippet.days_remaining <= 7 ? '#d94a4a' : theme.muted
                          }}>
                            {snippet.days_remaining}d remaining
                          </span>
                          <div className="snippet-actions" style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button onClick={() => restoreSnippet(snippet.id)} className="btn btn-secondary" title="Restore"
                              style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              <RotateCcw size={13} />
                            </button>
                            <button onClick={() => permanentDeleteSnippet(snippet.id)} className="btn btn-danger" title="Delete permanently"
                              style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            )}

            {/* Backups View */}
            {view === 'backups' && user?.role === 'superuser' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h2 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '4px' }}>Backups</h2>
                    <p style={{ color: theme.muted, fontSize: '13px' }}>Create, download, and restore database backups. Files are gzipped SQLite databases.</p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: backupUploading ? 0.5 : 1 }}>
                      <Upload size={16} /> {backupUploading ? 'Restoring...' : 'Upload & Restore'}
                      <input
                        type="file"
                        accept=".gz,.sqlite,.db"
                        style={{ display: 'none' }}
                        disabled={backupUploading}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            restoreBackup(e.target.files[0]);
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                    <button onClick={createBackup} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Plus size={16} /> Create Backup
                    </button>
                  </div>
                </div>

                {/* Settings panel */}
                <div style={{
                  display: 'flex', gap: '16px', marginBottom: '24px', padding: '16px',
                  background: theme.card, borderRadius: '12px', border: `1px solid ${theme.border}`,
                  alignItems: 'flex-end', flexWrap: 'wrap'
                }}>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: theme.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                      Auto-backup schedule
                    </label>
                    <select
                      className="filter-select"
                      style={{ width: '100%', height: '38px' }}
                      value={backupSettings.scheduleHours}
                      onChange={(e) => setBackupSettings(s => ({ ...s, scheduleHours: Number(e.target.value) }))}
                    >
                      <option value={0}>Disabled</option>
                      <option value={1}>Every hour</option>
                      <option value={6}>Every 6 hours</option>
                      <option value={12}>Every 12 hours</option>
                      <option value={24}>Daily</option>
                      <option value={168}>Weekly</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: theme.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                      Backups to keep
                    </label>
                    <select
                      className="filter-select"
                      style={{ width: '100%', height: '38px' }}
                      value={backupSettings.maxBackups}
                      onChange={(e) => setBackupSettings(s => ({ ...s, maxBackups: parseInt(e.target.value) }))}
                    >
                      {[5, 10, 15, 20, 30, 50, 100].map(n => (
                        <option key={n} value={n}>{n} backups</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => saveBackupSettings(backupSettings)}
                    className="btn btn-primary"
                    style={{ height: '38px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Save size={14} /> Save Settings
                  </button>
                </div>

                {backupsLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: theme.muted }}>Loading...</div>
                ) : backups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px' }}>
                    <HardDrive size={48} color={theme.muted} style={{ marginBottom: '16px', opacity: 0.3 }} />
                    <p style={{ color: theme.muted, fontSize: '16px', fontWeight: '600' }}>No backups yet</p>
                    <p style={{ color: theme.muted, fontSize: '13px', marginTop: '4px' }}>Create your first backup to get started</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div className="list-view-header" style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 100px 180px 150px',
                      gap: '16px',
                      padding: '10px 16px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: theme.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: `2px solid ${theme.border}`,
                      userSelect: 'none'
                    }}>
                      <span>Filename</span>
                      <span>Size</span>
                      <span>Created</span>
                      <span style={{ textAlign: 'right' }}>Actions</span>
                    </div>
                    {backups.map(backup => (
                      <div
                        key={backup.name}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 100px 180px 150px',
                          gap: '16px',
                          alignItems: 'center',
                          padding: '10px 16px',
                          background: theme.card,
                          borderRadius: '8px',
                          border: `1px solid ${theme.border}`,
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = themeMode === 'dark' ? '#1e1e23' : '#f8f7ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = theme.card; }}
                      >
                        <span style={{
                          fontWeight: '600', fontSize: '13px', fontFamily: 'JetBrains Mono, monospace',
                          color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {backup.name}
                        </span>
                        <span className="list-view-col-hide" style={{ fontSize: '12px', color: theme.muted }}>
                          {(backup.size / 1024).toFixed(1)} KB
                        </span>
                        <span className="list-view-col-hide" style={{ fontSize: '12px', color: theme.muted }}>
                          {new Date(backup.created).toLocaleString()}
                        </span>
                        <div className="snippet-actions" style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <button onClick={() => restoreBackupFromServer(backup.name)} className="btn btn-secondary" title="Restore"
                            style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                            <RotateCcw size={13} />
                          </button>
                          <button onClick={() => downloadBackup(backup.name)} className="btn btn-secondary" title="Download"
                            style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                            <Download size={13} />
                          </button>
                          <button onClick={() => deleteBackup(backup.name)} className="btn btn-danger" title="Delete"
                            style={{ padding: '5px', minWidth: '28px', height: '28px' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div >

      {/* Password Change Modal */}
      {
        showPasswordModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: theme.card,
              padding: '32px',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '400px',
              boxShadow: theme.shadowLg,
              border: `1px solid ${theme.border} `
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Change Password</h2>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer' }}
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    className="input-field"
                    required
                    value={passwordForm.oldPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    className="input-field"
                    required
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    className="input-field"
                    required
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ marginTop: '8px', justifyContent: 'center' }}>
                  Update Password
                </button>
              </form>
            </div>
          </div>
        )
      }
      {/* Group Members Modal (Existing) ... */}

      {/* Version History Modal */}
      {
        snippetVersions && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}>
            <div className="snippet-card" style={{ maxWidth: '800px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ background: `${theme.accent}15`, padding: '8px', borderRadius: '8px' }}>
                    <History size={20} color={theme.accent} />
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: '800' }}>Version History</h3>
                </div>
                <button
                  onClick={() => setSnippetVersions(null)}
                  className="btn btn-secondary"
                  style={{ padding: '8px', width: '40px', height: '40px' }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingRight: '8px' }}>
                {snippetVersions.versions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: theme.muted }}>
                    <p>No previous versions recorded for this snippet.</p>
                  </div>
                ) : (
                  snippetVersions.versions.map((v, i) => {
                    const currentSnippet = data.snippets.find(s => s.id === snippetVersions.snippetId);
                    const currentCode = currentSnippet ? currentSnippet.code : "";
                    return (
                      <div key={v.id} style={{
                        padding: '16px',
                        background: theme.bg,
                        borderRadius: '12px',
                        border: `1px solid ${theme.border} `,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{ fontWeight: '700', fontSize: '15px', color: theme.text }}>
                              {v.title} <span style={{ fontSize: '12px', color: theme.muted, fontWeight: '500' }}>#{snippetVersions.versions.length - i}</span>
                            </p>
                            <p style={{ fontSize: '12px', color: theme.muted, marginTop: '2px' }}>
                              Changed by <span style={{ fontWeight: '600' }}>{getUserDisplayName(v.changed_by)}</span> on {new Date(v.changed_at).toLocaleString()}
                            </p>
                          </div>
                          <button
                            onClick={() => restoreVersion(snippetVersions.snippetId, v.id)}
                            className="btn btn-primary"
                            style={{ padding: '8px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </div>

                        <div style={{
                          borderRadius: '12px',
                          margin: 0,
                          background: themeMode === 'dark' ? '#0f172a' : '#1e293b',
                          overflowX: 'auto',
                          border: `1px solid ${theme.border}`,
                          fontSize: '13px',
                          lineHeight: '1.6',
                          color: '#e2e8f0',
                          fontFamily: 'JetBrains Mono, monospace',
                          maxHeight: '450px',
                          overflowY: 'auto',
                          boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
                        }}>
                          {(() => {
                            const diff = Diff.diffLines(v.code, currentCode);
                            return diff.map((part, index) => (
                              <div
                                key={index}
                                style={{
                                  background: part.added ? 'rgba(16, 185, 129, 0.15)' : part.removed ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
                                  borderLeft: `4px solid ${part.added ? theme.success : (part.removed ? theme.danger : 'transparent')}`,
                                  padding: '4px 16px',
                                  display: 'flex',
                                  transition: 'background 0.2s ease'
                                }}
                              >
                                <span style={{
                                  minWidth: '24px',
                                  userSelect: 'none',
                                  color: part.added ? theme.success : (part.removed ? theme.danger : theme.muted),
                                  fontWeight: 'bold',
                                  opacity: 0.8
                                }}>
                                  {part.added ? '+' : part.removed ? '-' : ' '}
                                </span>
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: part.added ? '#a7f3d0' : (part.removed ? '#fecaca' : '#e2e8f0') }}>
                                  {part.value}
                                </pre>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: `1px solid ${theme.border} `, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setSnippetVersions(null)} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Confirmation Modal */}
      {
        confirmModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100, backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: theme.card, padding: '32px', borderRadius: '16px',
              width: '100%', maxWidth: '400px', boxShadow: theme.shadowLg, border: `1px solid ${theme.border} `
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '12px' }}>{confirmModal.title}</h2>
              <p style={{ color: theme.muted, fontSize: '14px', marginBottom: '24px', lineHeight: '1.5' }}>
                {confirmModal.message}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setConfirmModal(null)}
                  className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                  className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Snippet Preview Modal */}
      {previewSnippet && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }} onClick={() => setPreviewSnippet(null)}>
          <div style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '16px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: theme.shadowLg,
            overflow: 'hidden'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header bar — mimics Stitch code editor top bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: `1px solid ${theme.border}`,
              background: themeMode === 'dark' ? '#131316' : theme.bg
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: theme.danger }}></span>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }}></span>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: theme.success }}></span>
                </div>
                <span style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: theme.muted, fontWeight: 500 }}>
                  {previewSnippet.title}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => { handleCopy(previewSnippet.code, previewSnippet.id); }}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', height: 'auto' }}
                >
                  {copiedId === previewSnippet.id ? <Check size={14} color={theme.success} /> : <Copy size={14} />}
                  <span>Copy</span>
                </button>
                <button
                  onClick={() => { startEdit(previewSnippet); setPreviewSnippet(null); }}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', height: 'auto' }}
                >
                  <Edit3 size={14} /> Edit
                </button>
                <button
                  onClick={() => setPreviewSnippet(null)}
                  style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: '4px' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Metadata strip */}
            <div style={{
              padding: '12px 24px',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center',
              fontSize: '12px', color: theme.muted, fontWeight: 500
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Folder size={13} /> {previewSnippet.folder}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Code size={13} /> {previewSnippet.language}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Users size={13} /> {getUserDisplayName(previewSnippet.created_by)}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {previewSnippet.visibility === 'private' ? <Lock size={13} /> : previewSnippet.visibility === 'internal' ? <Shield size={13} /> : <Globe size={13} />}
                {previewSnippet.visibility}
              </span>
              {previewSnippet.tag && previewSnippet.tag.split(',').map(t => t.trim()).filter(t => t).map((tag, i) => (
                <span key={i} style={{
                  background: `${theme.accent}15`, color: theme.accent,
                  padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600
                }}>#{tag}</span>
              ))}
            </div>

            {/* Full code view */}
            <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
              <pre style={{
                margin: 0,
                padding: '24px',
                background: themeMode === 'dark' ? '#1a1a2e' : '#1e293b',
                color: '#e2e8f0',
                fontSize: '13px',
                lineHeight: '1.7',
                fontFamily: 'JetBrains Mono, monospace',
                minHeight: '200px',
                overflowX: 'auto'
              }}>
                <CodeWithImages code={previewSnippet.code} themeMode={themeMode} />
              </pre>
            </div>

            {/* Footer status bar */}
            <div style={{
              padding: '8px 24px',
              borderTop: `1px solid ${theme.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: themeMode === 'dark' ? '#131316' : theme.bg,
              fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', color: theme.muted,
              textTransform: 'uppercase', letterSpacing: '0.05em'
            }}>
              <span>{previewSnippet.code ? previewSnippet.code.split('\n').length : 0} lines</span>
              <span style={{ color: theme.accent, fontWeight: 700 }}>{previewSnippet.language}</span>
            </div>
          </div>
        </div>
      )}

      {/* What's New Popup */}
      {showWhatsNew && user && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1300,
          background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px', animation: 'fadeIn 0.3s ease'
        }} onClick={dismissWhatsNew}>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
            @keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
            @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
            @keyframes gradientShift { 0% { stop-color: ${theme.accent}; } 50% { stop-color: ${theme.accentHover}; } 100% { stop-color: ${theme.accent}; } }
          `}</style>
          <div style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '24px',
            width: '100%',
            maxWidth: '540px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: `0 40px 80px -20px rgba(0,0,0,0.4), 0 0 0 1px ${theme.accent}20`,
            animation: 'slideUp 0.4s ease'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Animated Header Graphic */}
            <div style={{
              position: 'relative', height: '180px', overflow: 'hidden',
              borderRadius: '24px 24px 0 0',
              background: `linear-gradient(135deg, ${theme.accent}20, ${theme.accentHover}10, ${theme.accent}08)`
            }}>
              {/* Animated SVG */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 540 180" fill="none">
                {/* Grid lines animation */}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                  <rect key={`h${i}`} x="40" y={20 + i * 18} width="460" height="1" fill={theme.accent} opacity="0.08">
                    <animate attributeName="opacity" values="0.04;0.12;0.04" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
                  </rect>
                ))}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                  <rect key={`v${i}`} x={40 + i * 46} y="20" width="1" height="140" fill={theme.accent} opacity="0.06">
                    <animate attributeName="opacity" values="0.03;0.1;0.03" dur={`${2.5 + i * 0.2}s`} repeatCount="indefinite" />
                  </rect>
                ))}
                {/* Floating snippet cards */}
                {[
                  { x: 60, y: 30, w: 130, h: 55, delay: '0s' },
                  { x: 210, y: 45, w: 130, h: 55, delay: '0.5s' },
                  { x: 360, y: 25, w: 130, h: 55, delay: '1s' },
                  { x: 135, y: 100, w: 130, h: 55, delay: '0.3s' },
                  { x: 285, y: 110, w: 130, h: 55, delay: '0.8s' }
                ].map((card, i) => (
                  <g key={i}>
                    <rect x={card.x} y={card.y} width={card.w} height={card.h} rx="8" fill={theme.card} stroke={theme.accent} strokeWidth="0.5" strokeOpacity="0.3">
                      <animate attributeName="y" values={`${card.y};${card.y - 6};${card.y}`} dur="3s" begin={card.delay} repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" begin={card.delay} repeatCount="indefinite" />
                    </rect>
                    {/* Code lines inside cards */}
                    {[0, 1, 2].map(j => (
                      <rect key={j} x={card.x + 10} y={card.y + 12 + j * 14} width={card.w - 20 - j * 20} height="4" rx="2" fill={theme.accent} opacity="0.15">
                        <animate attributeName="y" values={`${card.y + 12 + j * 14};${card.y + 6 + j * 14};${card.y + 12 + j * 14}`} dur="3s" begin={card.delay} repeatCount="indefinite" />
                      </rect>
                    ))}
                  </g>
                ))}
                {/* Sparkle dots */}
                {[
                  { cx: 50, cy: 90 }, { cx: 180, cy: 30 }, { cx: 320, cy: 150 },
                  { cx: 470, cy: 80 }, { cx: 500, cy: 140 }
                ].map((dot, i) => (
                  <circle key={i} cx={dot.cx} cy={dot.cy} r="2" fill={theme.accent}>
                    <animate attributeName="opacity" values="0;1;0" dur={`${1.5 + i * 0.4}s`} repeatCount="indefinite" />
                    <animate attributeName="r" values="1;3;1" dur={`${1.5 + i * 0.4}s`} repeatCount="indefinite" />
                  </circle>
                ))}
              </svg>
              {/* Version badge */}
              <div style={{
                position: 'absolute', top: '16px', right: '16px',
                background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
                color: '#fff', fontSize: '11px', fontWeight: '800',
                padding: '4px 12px', borderRadius: '20px',
                letterSpacing: '0.5px', fontFamily: 'JetBrains Mono, monospace'
              }}>
                {WHATS_NEW_VERSION}
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <h2 style={{ fontSize: '26px', fontWeight: '800', marginBottom: '8px', color: theme.text }}>
                  ✨ What's New
                </h2>
                <p style={{ color: theme.muted, fontSize: '14px', lineHeight: '1.6' }}>
                  Here's what we've been working on to improve your experience.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '28px' }}>
                {[
                  {
                    num: '01',
                    title: 'Symmetrical Grid Layout',
                    desc: 'Snippet cards now display in a clean, uniform grid for a polished, balanced look.',
                    icon: <LayoutGrid size={18} />
                  },
                  {
                    num: '02',
                    title: 'Comprehensive User Guide',
                    desc: 'The User Guide has been completely rewritten with detailed, step-by-step instructions for every feature.',
                    icon: <BookOpen size={18} />
                  },
                  {
                    num: '03',
                    title: 'Image Paste Support',
                    desc: 'Paste images directly into the code editor. They\'re uploaded and embedded as markdown automatically.',
                    icon: <Edit3 size={18} />
                  },
                  {
                    num: '04',
                    title: 'Database Backups',
                    desc: 'Create, download, and restore database backups with automatic scheduling support.',
                    icon: <HardDrive size={18} />
                  },
                  {
                    num: '05',
                    title: 'Recycle Bin',
                    desc: 'Deleted snippets are kept for 60 days and can be restored at any time.',
                    icon: <RotateCcw size={18} />
                  }
                ].map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: '14px', alignItems: 'flex-start',
                    padding: '14px 16px', borderRadius: '12px',
                    background: themeMode === 'dark' ? '#1a1a1f' : '#f8f7ff',
                    border: `1px solid ${theme.border}`,
                    transition: 'all 0.2s',
                    animation: `slideUp ${0.3 + i * 0.08}s ease`
                  }}>
                    <div style={{
                      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
                      color: '#fff', fontWeight: '800', fontSize: '10px',
                      width: '32px', height: '32px', borderRadius: '10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, boxShadow: `0 4px 12px ${theme.accent}30`
                    }}>
                      {feature.icon}
                    </div>
                    <div>
                      <p style={{ fontWeight: '700', fontSize: '14px', color: theme.text, marginBottom: '3px' }}>{feature.title}</p>
                      <p style={{ fontSize: '12px', color: theme.muted, lineHeight: '1.5' }}>{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={dismissWhatsNew}
                style={{
                  width: '100%', padding: '14px',
                  background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
                  color: '#fff', border: 'none', borderRadius: '14px',
                  fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: `0 8px 24px -8px ${theme.accent}50`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
                onMouseEnter={(e) => { e.target.style.filter = 'brightness(1.1)'; e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.target.style.filter = 'none'; e.target.style.transform = 'none'; }}
              >
                <Sparkles size={18} /> Got it, let's go!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Notification */}
      {
        notification && (() => {
          const notificationBg = notification.type === 'error' ? theme.danger : (notification.type === 'success' ? theme.success : theme.accent);
          return (
            <div style={{
              position: 'fixed', top: '24px', right: '24px',
              background: notificationBg,
              color: getContrastColor(notificationBg),
              padding: '12px 24px', borderRadius: '12px',
              boxShadow: theme.shadowLg, zIndex: 1200, display: 'flex', alignItems: 'center', gap: '10px',
              animation: 'slideIn 0.3s ease-out', fontWeight: '600', fontSize: '14px'
            }}>
              {notification.type === 'error' ? <X size={18} /> : (notification.type === 'success' ? <Check size={18} /> : <Terminal size={18} />)}
              {notification.message}
              <style>{`
                @keyframes slideIn {
                  from { transform: translateX(100%); opacity: 0; }
                  to { transform: translateX(0); opacity: 1; }
                }
              `}</style>
            </div>
          );
        })()
      }
    </div >
  );
}